<template>
  <BaseDialog
    v-if="open"
    :key="dialogKey"
    :open="open"
    :disabled="loading"
    :max-width="fullscreen ? '100vw' : '1400px'"
    :class="[
      {'as-fullscreen': fullscreen},
      {'hide-footer': viewMode === 'touch'}
    ]"
    class="attendance-dialog"
    @close="$emit('close')"
  >
    <template #title>
      Anwesenheiten
      — <strong>{{ eventTitle || '' }}</strong>
      <span v-if="teacherName"> - {{ teacherName }}</span>
      <span class="muted"> ({{ mandatory ? 'Pflichtangebot' : 'freiwilliges Angebot' }})</span>
      <span
        v-if="eventTimesText"
        class="muted"
      > — {{ eventTimesText }} — </span>
      <span class="font-bold">{{ currentlyPresentCount }} Schüler anwesend</span>
      <!-- Vollbild-Toggle: steht im Header links neben dem Close -->
      <button
        class="icon-btn title-expand"
        :aria-pressed="fullscreen ? 'true' : 'false'"
        :title="fullscreen ? 'Fenster verkleinern' : 'Auf Bildschirmgröße vergrößern'"
        aria-label="Vollbild umschalten"
        :disabled="isReadOnly"
        @click="toggleFullscreen"
      >
        <i
          :class="fullscreen ? 'pi pi-window-minimize' : 'pi pi-window-maximize'"
          aria-hidden="true"
        />
      </button>
      <!-- Hilfe-Button: direkt links neben dem Close -->
      <UiInfoButton
        class="title-help"
        title="Hilfe: Anwesenheiten"
        :width="520"
        :append-to-body="true"
        style="position:absolute; right:75px; top:50%; transform:translateY(-50%); height:38px; display:inline-flex; align-items:center;"
      >
        <ul>
          <li>Suche nach Schülern und filtern nach Kommen/Gehen/Fehlzeit.</li>
          <li>
            Kommen/Gehen per Checkbox auswählen; Zeiten unten über die Toolbar für die Auswahl setzen (Chronologie
            wird geprüft).
          </li>
          <li>Mehrfach setzen: Zeiten für die ausgewählten Zeilen über die Toolbar übernehmen.</li>
          <li>Meldung vorhanden: Zeile ist markiert und Interaktionen sind eingeschränkt.</li>
          <li>Vollbild über das Fenster-Icon; Schließen über den Button rechts oben.</li>
        </ul>
      </UiInfoButton>
    </template>
    <p
      v-if="error"
      class="notice notice-danger mt-12"
      role="alert"
    >
      {{ error }}
    </p>

    <!-- Filterleiste fix vor der Tabelle (nicht im horizontalen Overflow) -->
    <div class="att-filters">
      <input
        v-model.trim="searchTerm"
        type="text"
        class="input-slim"
        :placeholder="'Schüler suchen…'"
        aria-label="Schüler suchen"
        :disabled="isReadOnly"
      >

      <span class="muted small filter-item">anwesend</span>
      <select
        v-model="presentFilter"
        class="select-slim text-center"
        aria-label="Filter: Kommen"
        :disabled="isReadOnly"
      >
        <option value="">
          Alle
        </option>
        <option value="yes">
          Ja
        </option>
        <option value="no">
          Nein
        </option>
      </select>

      <button
        class="btn btn-primary btn-slim filter-item"
        :disabled="isReadOnly || !hasActiveFilters"
        title="Alle Filter zurücksetzen"
        @click="resetFilters"
      >
        Reset
      </button>

      <button
        class="btn btn-slim ml-5"
        :disabled="isReadOnly"
        :title="viewMode === 'bulk' ? 'Touch-Modus (iPad) aktivieren' : 'Standard-Modus aktivieren'"
        style="background-color: lightgoldenrodyellow;"
        @click="toggleViewMode"
      >
        {{ viewMode === 'bulk' ? 'Touch-Modus' : 'Listen-Modus' }}
      </button>
    </div>

    <div
      ref="attTable"
      class="att-table"
      :class="{'is-fullscreen': fullscreen}"
    >
      <div
        class="att-header"
        :style="{ gridTemplateColumns: gridCols }"
      >
        <div class="sticky-col sticky-name">
          Schüler
        </div>

        <template
          v-for="(n, bi) in attendanceBlocksCount"
          :key="`h-${bi}`"
        >
          <div
            class="col-head"
            style="align-items: center;"
          >
            <span class="col-label muted small text-center">letztes Gehen vor<br>Kommen {{ bi + 1 }}</span>
          </div>

          <div
            class="col-head col-time"
            :style="[viewMode === 'bulk' ? '': 'display: inline; text-align: center;']"
          >
            <span class="col-label muted small">Kommen {{ bi + 1 }}</span>
            <span
              v-if="viewMode === 'bulk'"
              class="col-tools"
            >
              <button
                class="icon-btn"
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (mit Kommen-Zeit)"
                aria-label="Auswahl umschalten (mit Kommen-Zeit)"
                @click="headerTogglePresentByHasTimeIdx(bi, true)"
              >
                <i
                  class="pi pi-clock"
                  aria-hidden="true"
                />
              </button>
              <button
                class="icon-btn"
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (ohne Kommen-Zeit)"
                aria-label="Auswahl umschalten (ohne Kommen-Zeit)"
                @click="headerTogglePresentByHasTimeIdx(bi, false)"
              >
                <i
                  class="pi pi-hourglass"
                  aria-hidden="true"
                />
              </button>
              <button
                class="icon-btn "
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (alle im Block)"
                aria-label="Auswahl umschalten (alle im Block)"
                @click="headerTogglePresentAllIdx(bi)"
              >
                <i
                  class="pi pi-refresh"
                  aria-hidden="true"
                />
              </button>
            </span>
          </div>

          <div
            class="col-head col-time"
            :style="[viewMode === 'bulk' ? '': 'display: inline; text-align: center;']"
          >
            <span
              class="col-label muted small"
              style="padding: 0 10px;"
            >Gehen {{ bi + 1 }}</span>

            <span
              class="col-tools"
            >
              <button
                v-if="viewMode === 'bulk'"
                class="icon-btn"
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (mit Kommen-Zeit)"
                aria-label="Auswahl umschalten (mit Kommen-Zeit)"
                @click="headerToggleLeftByHasTimeIdx(bi, true)"
              >
                <i
                  class="pi pi-clock"
                  aria-hidden="true"
                />
              </button>
              <button
                v-if="viewMode === 'bulk'"
                class="icon-btn"
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (mit Gehen-Zeit)"
                aria-label="Auswahl umschalten (mit Gehen-Zeit)"
                @click="headerToggleLeftByHasTimeIdx(bi, false)"
              >
                <i
                  class="pi pi-hourglass"
                  aria-hidden="true"
                />
              </button>
              <button
                v-if="viewMode === 'bulk'"
                class="icon-btn"
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (alle im Block)"
                aria-label="Auswahl umschalten (alle im Block)"
                @click="headerToggleLeftAllIdx(bi)"
              >
                <i
                  class="pi pi-refresh"
                  aria-hidden="true"
                />
              </button>
              <button
                v-if="bi === attendanceBlocksCount - 1"
                class="icon-btn"
                :disabled="isReadOnly || loading"
                title="Anwesenheitsblock hinzufügen"
                aria-label="Anwesenheitsblock hinzufügen"
                style="background: var(--primary); color: white;"
                @click="addBlockForAll"
              >
                <i
                  class="pi pi-plus"
                  aria-hidden="true"
                />
              </button>
            </span>
          </div>
        </template>

        <!-- NEU: Entlassen-Spalte für Listen-Modus (bulk)
        <div
            v-if="viewMode === 'bulk'"
            class="col-head col-time"
            style="text-align:center;"
        >
          <span class="col-label muted small">Entlassen</span>
        </div>-->

        <!-- Bestehende Touch-Spalte -->
        <div
          class="col-head col-actions"
          style="min-width: 70px; display: inline; text-align: right; margin-top: -5px;"
        >
          <div
            style="background: whitesmoke; display: inline-flex; padding: 3px 10px; flex-direction: column; align-items: center;"
            :style="[viewMode !== 'touch' ? 'width: 115px;' : '']"
          >
            <span
              class="col-label muted small"
              style="padding: 2px 0;"
              :style="[viewMode === 'touch' ? 'padding: 4px 10px;' : '']"
            >
              Entlassen</span>
            <span class="col-tools">
              <button
                v-if="viewMode === 'bulk'"
                class="icon-btn"
                :disabled="isReadOnly || loading || filteredRows.length === 0"
                title="Auswahl umschalten (alle im Block)"
                aria-label="Auswahl umschalten (alle im Block)"
                @click="headerToggleReleaseAll"
              >
                <i
                  class="pi pi-refresh"
                  aria-hidden="true"
                />
              </button>
            </span>
          </div>
        </div>
      </div>

      <div
        class="att-body"
        :class="{'fullscreen': fullscreen}"
      >
        <div
          v-for="row in filteredRows"
          :key="row.id"
          class="att-row"
          :class="{
            'row-warn': row.notifyAccepted,
            'row-selected-inout': isRowInOutSelected(row),
            'row-selected-release': isRowReleaseSelected(row)
          }"
          :style="{ gridTemplateColumns: gridCols }"
        >
          <div
            class="sticky-col sticky-name"
            :style="{ transform: `translateX(${tableScrollLeft}px)` }"
          >
            <span class="name-line">
              {{ row.name }}
            </span>

            <button
              type="button"
              class="icon-btn remove-student-btn ml-3"
              :disabled="isReadOnly || loading || row.busy"
              title="Schüler aus dem Angebot entfernen"
              aria-label="Schüler aus dem Angebot entfernen"
              @click="openRemoveStudentConfirm(row)"
            >
              <i
                class="pi pi-user-minus"
                aria-hidden="true"
              />
            </button>

            <button
              v-if="row.notifyAccepted"
              type="button"
              class="badge-notice"
              :title="row.notifySubject"
              @click="openNotification(row)"
            >
              {{ row.notifySubject || 'Meldung' }}
              <template v-if="row.notifyPeriod">
                ({{ row.notifyPeriod }})
              </template>
            </button>
          </div>

          <!-- Dynamische Anwesenheitsblöcke (je 2 Spalten: Kommen/Gehen) -->
          <template v-if="!isBlocked(row.id)">
            <template
              v-for="(n, bi) in attendanceBlocksCount"
              :key="`rb-${row.id}-${bi}`"
            >
              <!-- Letztes Gehen (nur Anzeige) -->
              <div class="inline-flex last-out">
                <span class="time-text muted">
                  {{ prevOutText(row, bi) }}
                </span>
              </div>
              <!-- Kommen -->
              <div class="inline-flex col-time">
                <!-- Standard-Modus: Checkboxen -->
                <label
                  v-if="viewMode === 'bulk'"
                  class="chk"
                >
                  <input
                    v-if="bi === 0"
                    v-model="row.selIn"
                    type="checkbox"
                    :disabled="isReadOnly || row.busy"
                    @change="onSelectIn(row, 0)"
                  >
                  <input
                    v-else
                    v-model="(row.blocks[bi-1] || (row.blocks[bi-1] = {present:false,left:false,selIn:false,selOut:false,checkedInTime:'',checkedOutTime:''})).selIn"
                    type="checkbox"
                    :disabled="isReadOnly || row.busy || !canInForBlock(row, bi)"
                    @change="onSelectIn(row, bi)"
                  >
                  <span class="time-text muted">
                    {{ bi === 0 ? (row.checkedInTime || '') : ((row.blocks[bi - 1]?.checkedInTime) || '') }}
                  </span>
                </label>

                <!-- Touch-Modus: großer Button öffnet Zeitdialog -->
                <button
                  v-else
                  type="button"
                  class="att-cell-btn"
                  :disabled="isReadOnly || row.busy || (bi !== 0 && !canInForBlock(row, bi))"
                  :title="'Kommen-Zeit setzen (' + (bi + 1) + ')'"
                  :class="[bi === 0 ? (row.checkedInTime ? 'checkedTime':'') : ((row.blocks[bi - 1]?.checkedInTime)? 'checkedTime':'')]"
                  @click="openTimeDialog(row, bi, 'in')"
                >
                  <span class="att-cell-btn__label">Kommen</span>
                  <span class="att-cell-btn__time muted">
                    {{ bi === 0 ? (row.checkedInTime || '—') : ((row.blocks[bi - 1]?.checkedInTime) || '—') }}
                  </span>
                </button>
              </div>
              <!-- Gehen -->
              <div class="inline-flex col-time">
                <!-- Standard-Modus: Checkboxen -->
                <label
                  v-if="viewMode === 'bulk'"
                  class="chk"
                >
                  <input
                    v-if="bi === 0"
                    v-model="row.selOut"
                    type="checkbox"
                    :disabled="isReadOnly || row.busy || !row.present"
                    @change="onSelectOut(row, 0)"
                  >
                  <input
                    v-else
                    v-model="(row.blocks[bi-1] || (row.blocks[bi-1] = {present:false,left:false,selIn:false,selOut:false,checkedInTime:'',checkedOutTime:''})).selOut"
                    type="checkbox"
                    :disabled="isReadOnly || row.busy || !(row.blocks[bi-1]?.present)"
                    @change="onSelectOut(row, bi)"
                  >
                  <span class="time-text muted">
                    {{ bi === 0 ? (row.checkedOutTime || '') : ((row.blocks[bi - 1]?.checkedOutTime) || '') }}
                  </span>
                </label>

                <!-- Touch-Modus: großer Button öffnet Zeitdialog -->
                <button
                  v-else
                  type="button"
                  class="att-cell-btn"
                  :disabled="isReadOnly || row.busy || (bi === 0 ? !row.present : !(row.blocks?.[bi - 1]?.present))"
                  :title="'Gehen-Zeit setzen (' + (bi + 1) + ')'"
                  :class="[bi === 0 ? (row.checkedOutTime ? 'checkedTime':'') : ((row.blocks[bi - 1]?.checkedOutTime)? 'checkedTime':'')]"
                  @click="openTimeDialog(row, bi, 'out')"
                >
                  <span class="att-cell-btn__label">Gehen</span>
                  <span class="att-cell-btn__time muted">
                    {{ bi === 0 ? (row.checkedOutTime || '—') : ((row.blocks[bi - 1]?.checkedOutTime) || '—') }}
                  </span>
                </button>
              </div>
            </template>

            <!-- ÜBERARBEITET: Entlassen-Auswahl (Listen-Modus) -->
            <div
              v-if="viewMode === 'bulk'"
              class="inline-flex att-row-actions-right"
              style="justify-content: flex-end;"
            >
              <div
                class="release-cell"
                :class="{'is-released': isReleased(row)}"
              >
                <label class="chk release-chk">
                  <input
                    v-model="row.selRelease"
                    type="checkbox"
                    :disabled="isReleaseCheckboxDisabled(row)"
                    aria-label="Schüler für Entlassen auswählen"
                  >
                  <span
                    v-if="isReleased(row)"
                    class="time-text release-time"
                    :title="'Entlassen um ' + (row.schoolReleasedAtText || '')"
                  >
                    {{ row.schoolReleasedAtText || '' }}
                  </span>
                </label>
              </div>
            </div>
          </template>

          <!-- Blockiert: „Übernehmen“-Info über alle Blockspalten spannen -->
          <div
            v-else
            class="inline"
            :style="{ gap: '6px', gridColumn: `span ${(attendanceBlocksCount * 3) + (viewMode === 'bulk' ? 1 : 0)}` }"
          >
            <span
              v-if="availabilityMap.get(row.id)?.since || availabilityMap.get(row.id)?.eventTitle"
              class="muted small"
              style="display:block; line-height:1.3;"
            >
              seit {{ formatSince(availabilityMap.get(row.id)?.since) }}
              <template v-if="availabilityMap.get(row.id)?.eventTitle">
                in {{ availabilityMap.get(row.id)?.eventTitle }}
              </template>
            </span>
            <button
              class="btn btn-primary btn-slim"
              type="button"
              :disabled="isReadOnly || loading || row.busy"
              title="Schüler aus dem vorherigen Angebot in dieses Angebot übernehmen"
              @click="openTakeoverConfirm(row)"
            >
              Übernehmen in dieses Angebot
            </button>
          </div>

          <!-- NEU: ganz rechts in der Row (eigene Grid-Spalte) -->
          <div
            v-if="viewMode === 'touch'"
            class="inline-flex att-row-actions-right"
          >
            <template v-if="isReleased(row)">
              <span
                class="time-text muted"
                style="margin-right:10px; width: 60px; text-align: center; font-weight: 600; color: var(--warn);"
              >
                {{ row.schoolReleasedAtText || '' }}
              </span>
            </template>
            <button
              v-else
              type="button"
              class="btn release-touch-btn"
              :disabled="isReleaseDisabled(row)"
              title="Schüler entlassen"
              aria-label="Schüler entlassen"
              @click="openReleaseDialog(row)"
            >
              <i
                class="pi pi-sign-out"
                aria-hidden="true"
              />
            </button>
          </div>
        </div>

        <p
          v-if="!loading && rows.length === 0"
          class="muted att-empty"
        >
          Keine Teilnehmer.
        </p>
      </div>
    </div>

    <div class="toolbar">
      <div class="add-student bulk-actions">
        <div class="bulk-actions-item">
          <span class="muted small">Schüler hinzufügen</span>
          <span class="white-space-nowrap actions-item">
            <select
              v-model="selectedCandidateId"
              class="select-slim"
              style="width: 210px;"
              :disabled="isReadOnly || loading || loadingCandidates || candidateStudents.length === 0"
              aria-label="Aktive Schüler (nicht gebucht)"
              title="Aktive Schüler (nicht gebucht)"
            >
              <option
                :value="''"
                disabled
              >
                — Schüler wählen —
              </option>
              <option
                v-for="c in candidateStudents"
                :key="c.id"
                :value="String(c.id)"
              >
                {{ c.name }}
              </option>
            </select>
            <button
              class="btn btn-primary btn-slim"
              :disabled="isReadOnly || loading || loadingCandidates || !selectedCandidateId"
              title="Ausgewählten Schüler zum Angebot hinzufügen"
              @click="addStudent"
            >
              <i
                class="pi pi-save"
                aria-hidden="true"
              />
            </button>
          </span>
        </div>
      </div>

      <div
        v-if="viewMode === 'bulk'"
        class="bulk-actions"
        aria-label="Mehrfach setzen"
      >
        <div class="bulk-actions-item mr-4">
          <span class="muted small">Kommen</span>
          <span class="white-space-nowrap actions-item">
            <FlatPickr
              :key="`bulk-in-${dateTimeMode ? 'dt' : 't'}`"
              v-model="bulkInValue"
              :config="dateTimeMode ? dateTimeConfig : timeConfig"
              class="flatpickr-datetime"
              :class="{ 'only--time': !dateTimeMode }"
              :placeholder="dateTimeMode ? 'TT.MM.JJJJ HH:MM' : 'HH:MM'"
              :disabled="isReadOnly"
              @on-open="() => prefillNowIfEmptyBulk('in')"
            />
            <button
              class="btn icon-btn"
              :disabled="isReadOnly || loading || !bulkInValue"
              title="Kommen-Element leeren"
              @click="applyBulkInClear"
            >
              <i
                class="pi pi-eraser"
                aria-hidden="true"
              />
            </button>
            <button
              class="btn btn-primary btn-slim"
              :disabled="isReadOnly || loading || !selectedAnyIn"
              title="Kommen-Zeit für ausgewählte speichern"
              @click="applyBulkIn"
            >
              <i
                class="pi pi-save"
                aria-hidden="true"
              />
            </button>
          </span>
        </div>

        <div class="bulk-actions-item">
          <span class="muted small">Gehen</span>
          <span class="white-space-nowrap actions-item">
            <FlatPickr
              :key="`bulk-out-${dateTimeMode ? 'dt' : 't'}`"
              v-model="bulkOutValue"
              :config="dateTimeMode ? dateTimeConfig : timeConfig"
              class="flatpickr-datetime"
              :class="{ 'only--time': !dateTimeMode }"
              :placeholder="dateTimeMode ? 'TT.MM.JJJJ HH:MM' : 'HH:MM'"
              :disabled="isReadOnly"
              @on-open="() => prefillNowIfEmptyBulk('out')"
            />
            <button
              class="btn icon-btn"
              :disabled="isReadOnly || loading || !bulkOutValue"
              title="Gehen-Element leeren"
              @click="applyBulkOutClear"
            >
              <i
                class="pi pi-eraser"
                aria-hidden="true"
              />
            </button>
            <button
              class="btn btn-primary btn-slim"
              :disabled="isReadOnly || loading || !selectedAnyOut"
              title="Gehen-Zeit für ausgewählte speichern (nur für Anwesende)"
              @click="applyBulkOut"
            >
              <i
                class="pi pi-save"
                aria-hidden="true"
              />
            </button>
          </span>
        </div>
        <div
          class="bulk-actions-item release-actions-items"
          style="margin-left: auto; align-items: flex-end;"
        >
          <template v-if="isSameDayToday">
            <div class="bulk-actions-item-inner">
              <span class="muted small">Entlassen</span>
              <span
                class="white-space-nowrap actions-item"
                style="justify-content: flex-end;"
              >
                <FlatPickr
                  :key="`bulk-release-${dateTimeMode ? 'dt' : 't'}`"
                  v-model="bulkReleaseTimeValue"
                  :config="dateTimeMode ? dateTimeConfig : timeConfig"
                  class="flatpickr-datetime"
                  :class="{ 'only--time': !dateTimeMode }"
                  :placeholder="dateTimeMode ? 'TT.MM.JJJJ HH:MM' : 'HH:MM'"
                  :disabled="isReadOnly || loading || !selectedAnyRelease"
                  @on-open="onBulkReleaseTimeOpen"
                />

                <button
                  class="btn icon-btn"
                  :disabled="isReadOnly || loading || !selectedAnyRelease || !bulkReleaseTimeValue"
                  title="Entlassen-Zeit leeren"
                  aria-label="Entlassen-Zeit leeren"
                  @click="applyBulkReleaseTimeClear"
                >
                  <i
                    class="pi pi-eraser"
                    aria-hidden="true"
                  />
                </button>

                <select
                  v-model.number="bulkReleaseReasonId"
                  class="select-slim select-release"
                  :disabled="isReadOnly || loading || !selectedAnyRelease"
                  aria-label="Grund für Entlassen"
                  title="Grund für Entlassen"
                >
                  <option
                    :value="0"
                    disabled
                  >
                    — Grund wählen —
                  </option>
                  <option
                    v-for="r in releaseReasonsForBulk"
                    :key="r.id"
                    :value="Number(r.id)"
                  >
                    {{ r.title }}
                  </option>
                </select>

                <button
                  class="btn btn-primary btn-slim"
                  :disabled="isReadOnly || loading || !canSaveBulkRelease"
                  title="Ausgewählte Schüler entlassen"
                  @click="applyBulkReleaseTime"
                >
                  <i
                    class="pi pi-save"
                    aria-hidden="true"
                  />
                </button>
              </span>
            </div>
          </template>

          <template v-else>
            <div
              class="muted small"
              role="status"
              style="max-width: 560px; background: whitesmoke; border: 1px solid var(--border); border-radius: 8px; padding: 8px 10px;"
            >
              <strong>Hinweis:</strong>
              Dieses Angebot liegt nicht am heutigen Tag. Entlassen erfordert eine Uhrzeit und ist hier nicht möglich.
              Bitte in den Touch‑Modus wechseln und dort pro Schüler mit Zeit bestätigen.
              <button
                type="button"
                class="btn btn-slim"
                style="margin-left: 8px; background-color: lightgoldenrodyellow;"
                @click="viewMode = 'touch'"
              >
                Touch‑Modus
              </button>
            </div>
          </template>
        </div>
      </div>

      <div
        v-if="viewMode === 'touch'"
        class="toolbar-actions touch-view"
      >
        <button
          v-if="canFinish"
          class="btn btn-warn"
          :disabled="isReadOnly || loading"
          @click="openCompleteConfirm"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          {{ status === 'completed' ? 'Abschluss bearbeiten' : 'Abschließen' }}
        </button>
        <button
          class="btn"
          :disabled="loading"
          @click="$emit('close')"
        >
          Schließen
        </button>
      </div>
    </div>

    <template
      v-if="viewMode === 'bulk'"
      #footer
    >
      <div class="toolbar-actions">
        <button
          v-if="canFinish"
          class="btn btn-warn"
          :disabled="isReadOnly || loading"
          @click="openCompleteConfirm"
        >
          <span
            v-if="loading"
            class="spinner"
            aria-hidden="true"
          />
          {{ status === 'completed' ? 'Abschluss bearbeiten' : 'Abschließen' }}
        </button>
        <button
          class="btn"
          :disabled="loading"
          @click="$emit('close')"
        >
          Schließen
        </button>
      </div>
    </template>
  </BaseDialog>

  <EventCompleteDialog
    :open="completeOpen"
    :event-id="eventId"
    :event-title="eventTitle"
    :event-mandatory="eventMandatory"
    :event-starts-at="eventStartsAt"
    :event-ends-at="eventEndsAt"
    @close="completeOpen=false"
    @completed="onCompletedFromCompleteDialog"
  />

  <NotificationViewDialog
    :open="notifViewOpen"
    :notification-id="notifViewId"
    @close="onNotificationDialogClose"
    @accept="onNotificationDialogEvent"
    @reject="onNotificationDialogEvent"
    @read="onNotificationDialogEvent"
    @create-correction="onNotifCreateCorrection"
  />

  <NotificationCreateDialog
    :open="createOpen"
    :notif-parent-id="createNotifParentId"
    :prefill-student-id="createPrefill.studentId"
    :prefill-subject="createPrefill.subject"
    :prefill-date-from="createPrefill.dateFrom"
    :prefill-date-to="createPrefill.dateTo"
    :prefill-time-from="createPrefill.timeFrom"
    :prefill-time-to="createPrefill.timeTo"
    :prefill-kind="createPrefill.kind"
    @close="onCreateDialogClose"
    @created="onCreateCreated"
  />

  <!-- NEU: Schüler entlassen -->
  <StudentReleaseConfirmDialog
    :open="releaseDialogOpen"
    :student="releaseDialogStudent"
    :occurred-at="releaseAtIso"
    @close="releaseDialogOpen=false"
    @released="onStudentReleased"
  />

  <!-- Bestätigungsdialog: Schüler übernehmen -->
  <BaseDialog
    v-if="takeoverConfirmOpen"
    :open="takeoverConfirmOpen"
    :max-width="'520px'"
    @close="onTakeoverCancel"
  >
    <template #title>
      Schüler übernehmen
    </template>

    <div
      class="inline"
      style="flex-direction: column; align-items: flex-start; gap: 8px;"
    >
      <p>
        Schüler „<strong>{{ takeoverRow?.name || '' }}</strong>“ aus dem vorherigen Angebot
        <template v-if="takeoverAvail?.eventTitle">
          „{{ takeoverAvail.eventTitle }}“
        </template>
        übernehmen?
      </p>
      <p class="muted small">
        Aktion: Im vorherigen Angebot wird Gehen auf <strong>{{ takeoverPlanned.checkoutTxt }}</strong> gesetzt;
        hier wird Kommen auf <strong>{{ takeoverPlanned.checkinTxt }}</strong> gesetzt.
      </p>
      <p
        v-if="takeoverAvail?.since"
        class="muted small"
      >
        Aktuell seit {{ formatSince(takeoverAvail.since) }} eingecheckt.
      </p>
    </div>

    <div
      class="row-actions"
      style="margin-top: 12px;"
    >
      <button
        class="btn"
        :disabled="takeoverBusy"
        @click="onTakeoverCancel"
      >
        Abbrechen
      </button>
      <button
        class="btn btn-primary"
        :disabled="takeoverBusy"
        @click="onTakeoverConfirm"
      >
        <span
          v-if="takeoverBusy"
          class="spinner"
          aria-hidden="true"
        />
        Übernehmen
      </button>
    </div>
  </BaseDialog>

  <!-- NEU: Bestätigungsdialog "Ankommen setzen" -->
  <BaseDialog
    v-if="arriveConfirmOpen"
    :open="arriveConfirmOpen"
    :max-width="'520px'"
    @close="onArriveConfirmCancel"
  >
    <template #title>
      Ankommen setzen
    </template>

    <div
      class="inline"
      style="flex-direction: column; align-items: flex-start; gap: 8px;"
    >
      <p>
        Der Schüler „<strong>{{ arriveConfirmRow?.name || '' }}</strong>“ ist für diesen Zeitpunkt
        noch nicht als in der Schule angekommen erfasst.
      </p>
      <p class="muted small">
        Soll ein Ankommen in der Schule automatisch gesetzt werden?
      </p>
      <p
        v-if="arriveConfirmTimeText"
        class="muted small"
      >
        Ankommen-Zeit: <strong>{{ arriveConfirmTimeText }}</strong>
        (1 Minute vor dem gewählten Kommen-Zeitpunkt)
      </p>
    </div>

    <div
      class="row-actions"
      style="margin-top: 12px;"
    >
      <button
        class="btn"
        :disabled="arriveConfirmBusy"
        @click="onArriveConfirmCancel"
      >
        Abbrechen
      </button>
      <button
        class="btn btn-primary"
        :disabled="arriveConfirmBusy"
        @click="onArriveConfirmConfirm"
      >
        <span
          v-if="arriveConfirmBusy"
          class="spinner"
          aria-hidden="true"
        />
        Speichern
      </button>
    </div>
  </BaseDialog>

  <!-- Touch-Zeitdialog (HH/MM) -->
  <BaseDialog
    v-if="timeDialogOpen"
    :open="timeDialogOpen"
    :max-width="'480px'"
    @close="closeTimeDialog"
  >
    <template #title>
      {{ timeDialogTitle }}
    </template>

    <div class="time-dlg">
      <p class="muted small">
        Zeit eingeben. Beim Antippen wird der Inhalt markiert.
      </p>

      <div class="time-dlg__row">
        <div class="field">
          <label class="muted small">Stunde (HH)</label>
          <input
            ref="timeHourEl"
            v-model="timeHour"
            inputmode="numeric"
            pattern="[0-9]*"
            class="time-dlg__input"
            maxlength="2"
            @focus="selectAllOnFocus"
          >
        </div>

        <div class="time-dlg__sep">
          :
        </div>

        <div class="field">
          <label class="muted small">Minute (MM)</label>
          <input
            ref="timeMinuteEl"
            v-model="timeMinute"
            inputmode="numeric"
            pattern="[0-9]*"
            class="time-dlg__input"
            maxlength="2"
            @focus="selectAllOnFocus"
          >
        </div>
      </div>

      <p
        v-if="timeDialogError"
        class="notice notice-danger mt-12"
        role="alert"
      >
        {{ timeDialogError }}
      </p>

      <div
        class="row-actions"
        style="border-top: 1px solid #eee; padding-top: 10px;"
      >
        <button
          class="btn time-dlg__remove-btn"
          :disabled="timeDialogBusy"
          title="Zeit entfernen"
          @click="clearTimeDialogValue"
        >
          Entfernen
        </button>

        <button
          class="btn"
          :disabled="timeDialogBusy"
          @click="closeTimeDialog"
        >
          Abbrechen
        </button>

        <button
          class="btn btn-primary"
          :disabled="timeDialogBusy"
          @click="saveTimeDialog"
        >
          <span
            v-if="timeDialogBusy"
            class="spinner"
            aria-hidden="true"
          />
          Speichern
        </button>
      </div>
    </div>
  </BaseDialog>

  <!-- NEU: Bestätigungsdialog "Schüler entfernen" -->
  <BaseDialog
    v-if="removeStudentConfirmOpen"
    :open="removeStudentConfirmOpen"
    :max-width="'560px'"
    @close="onRemoveStudentCancel"
  >
    <template #title>
      Schüler entfernen
    </template>

    <div
      class="inline"
      style="flex-direction: column; align-items: flex-start; gap: 8px;"
    >
      <p>
        Soll „<strong>{{ removeStudentRow?.name || '' }}</strong>“ aus diesem Angebot entfernt werden?
      </p>
      <p class="muted small">
        Die Teilnahme wird aus dem Angebot entfernt und alle zugehörigen Anwesenheits-Einträge
        (Kommen/Gehen inkl. Zusatzblöcke) werden gelöscht.
      </p>
    </div>

    <div
      class="row-actions"
      style="margin-top: 12px; width: 100%;"
    >
      <button
        class="btn"
        :disabled="removeStudentBusy"
        @click="onRemoveStudentCancel"
      >
        Abbrechen
      </button>
      <button
        class="btn btn-primary"
        :disabled="removeStudentBusy"
        @click="onRemoveStudentConfirm"
      >
        <span
          v-if="removeStudentBusy"
          class="spinner"
          aria-hidden="true"
        />
        Entfernen
      </button>
    </div>
  </BaseDialog>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, ref, watch} from 'vue';
import api from '@/services/api';
import BaseDialog from '@/components/BaseDialog.vue';
import {useToast} from 'primevue/usetoast';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';
import {German} from 'flatpickr/dist/l10n/de.js';
import EventCompleteDialog from "@/components/events/EventCompleteDialog.vue";
import UiInfoButton from '@/components/UiInfoButton.vue';
import NotificationViewDialog from '@/components/notifications/NotificationViewDialog.vue';
import NotificationCreateDialog from '@/components/notifications/NotificationCreateDialog.vue';
import StudentReleaseConfirmDialog from '@/components/dialogs/StudentReleaseConfirmDialog.vue';
import {useAuthStore} from '@/stores/auth';

// Vollbild-Zustand
const fullscreen = ref(false);

// Horizontaler Scroll-Sync: Tabellenref + scrollLeft
const attTable = ref(null);
const tableScrollLeft = ref(0);

function onTableScroll() {
  try {
    tableScrollLeft.value = attTable.value ? (attTable.value.scrollLeft || 0) : 0;
  } catch {
    tableScrollLeft.value = 0;
  }
}

// Availability-Status mit einfachem Timer-Polling (alle 10s)
const availabilityMap = ref(new Map()); // studentId -> { free, since, eventTitle, ... }
let availabilityTimer = null;
// Einmaliger Watcher, um initial zu laden, sobald rows befüllt sind
let availabilityRowsUnwatch = null;

// Helper: Blocked?
function isBlocked(studentId) {
  const a = availabilityMap.value.get(String(studentId)) || availabilityMap.value.get(Number(studentId));
  return a ? a.free === false : false;
}

// Helper: 'since' formatiert nach Berlin HH:MM
function formatSince(iso) {
  if (!iso) return '';
  const d = parseDbTsToDate(iso);
  if (Number.isNaN(d.getTime())) return '';

  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

// Backend: einmalig Availability laden (nur initiale Befüllung)
async function fetchAvailabilityOnce() {
  try {
    if (!props.eventId) return;
    const ids = (rows.value || []).map(r => Number(r.id));
    if (!ids.length) return;
    const res = await api.post('/attendance/availability', {
      eventId: getBaseEventId(props),
      studentIds: ids
    });
    const items = res?.data?.data || [];
    const map = new Map();
    for (const e of items) {
      map.set(String(e.studentId), e);
    }
    availabilityMap.value = map;
  } catch (e) {
    // ignore
  }
}

// Polling (Start/Stop)
function startAvailabilityPolling() {
  stopAvailabilityPolling();

  // Wenn rows bereits IDs enthält → sofort laden,
  // sonst einmalig beobachten und bei erster Befüllung laden
  const idsNow = (rows.value || []).map(r => r.id).filter(Boolean);
  if (idsNow.length > 0) {
    fetchAvailabilityOnce();
  } else {
    availabilityRowsUnwatch = watch(rows, (nv) => {
      const ids = (nv || []).map(r => r.id).filter(Boolean);
      if (ids.length > 0) {
        fetchAvailabilityOnce();
        if (availabilityRowsUnwatch) {
          availabilityRowsUnwatch();
          availabilityRowsUnwatch = null;
        }
      }
    });
  }

  // Alle 10s wiederholen
  availabilityTimer = setInterval(fetchAvailabilityOnce, 10000);
}

function stopAvailabilityPolling() {
  if (availabilityTimer) {
    clearInterval(availabilityTimer);
    availabilityTimer = null;
  }
  if (availabilityRowsUnwatch) {
    availabilityRowsUnwatch();
    availabilityRowsUnwatch = null;
  }
}

// Beim Unmount sicher beenden
onBeforeUnmount(() => {
  stopAvailabilityPolling();
});

onBeforeUnmount(() => {
  try {
    if (attTable.value) attTable.value.removeEventListener('scroll', onTableScroll);
  } catch {
    /* noop */
  }
});

// ------------------------------
// NEU: Bulk-Entlassen (Gründe + Save)
// ------------------------------
const releaseReasons = ref([]); // [{id,title,active?}]
const loadingReleaseReasons = ref(false);
const bulkReleaseReasonId = ref(0);

const releaseReasonsForBulk = computed(() => {
  const list = Array.isArray(releaseReasons.value) ? releaseReasons.value : [];
  return list
      .map(r => ({
        id: Number(r.id ?? r.reason_id ?? 0),
        title: String(r.title ?? r.name ?? '').trim()
      }))
      .filter(r => r.id > 0 && r.title.length > 0)
      .sort((a, b) => a.title.localeCompare(b.title, 'de'));
});

const selectedAnyRelease = computed(() => {
  const list = filteredRows.value || [];
  for (const r of list) {
    if (r?.selRelease && !isReleaseCheckboxDisabled(r)) return true;
  }
  return false;
});

function isReleaseCheckboxDisabled(row) {
  if (!row) return true;

  if (isReadOnly.value) return true;
  if (loading.value) return true;
  if (row.busy) return true;
  if (isBlocked(row.id)) return true;

  // Entlassen setzt voraus, dass eine Schul-Ankunft existiert
  if (!row.schoolArrivedAtIso) return true;

  return false;
}

async function loadReleaseReasons() {
  if (!props.open) return;

  loadingReleaseReasons.value = true;
  try {
    const res = await api.get('/attendance/reasons');
    const data = res?.data?.data ?? res?.data ?? [];
    releaseReasons.value = Array.isArray(data) ? data : [];
  } catch {
    releaseReasons.value = [];
  } finally {
    loadingReleaseReasons.value = false;
  }
}

const canSaveBulkRelease = computed(() => {
  if (isReadOnly.value) return false;
  if (loading.value) return false;
  if (!selectedAnyRelease.value) return false;

  const timeText = String(bulkReleaseTimeValue.value || '').trim();
  const hasTime = !!timeText;

  // NEU: Speichern auch erlauben, wenn Zeit leer ist UND mindestens eine selektierte Zeile bereits entlassen ist
  if (!hasTime) {
    const list = filteredRows.value || [];
    for (const r of list) {
      if (!r?.selRelease) continue;
      if (isReleaseCheckboxDisabled(r)) continue;
      if (isReleased(r)) return true;
    }
    return false;
  }

  // Bei "Entlassen/Update" ist reasonId Pflicht
  const reasonId = Number(bulkReleaseReasonId.value || 0);
  return !!reasonId;
});

async function applyBulkReleaseTime() {
  if (isReadOnly.value) return;
  if (loading.value) return;

  const visible = filteredRows.value || [];

  const timeText = String(bulkReleaseTimeValue.value || '').trim();
  const occurredAtIso = timeText
      ? toIsoFromInputValue(timeText, eventStartYmdBerlin.value || undefined)
      : null;

  // occurredAtIso === null => "nicht mehr entlassen"
  const isUnrelease = occurredAtIso === null;

  const reasonId = Number(bulkReleaseReasonId.value || 0);

  // Bei Entlassen/Update ist reasonId Pflicht; bei Unrelease nicht
  if (!isUnrelease && !reasonId) return;

  // NEU: auch bereits entlassene selektierte Rows mitnehmen
  const studentIds = visible
      .filter(r => {
        if (!r?.selRelease) return false;
        if (isReleaseCheckboxDisabled(r)) return false;
        if (r.busy) return false;
        if (isBlocked(r.id)) return false;

        // Unrelease nur auf bereits entlassene anwenden
        if (isUnrelease) return isReleased(r);

        // Release/Update auf alle selektierten anwenden (auch bereits entlassene)
        return true;
      })
      .map(r => Number(r.id))
      .filter(Boolean);

  if (!props.eventId || studentIds.length === 0) return;

  loading.value = true;
  error.value = '';

  try {
    await api.post('/attendance/release/bulk', {
      eventId: getBaseEventId(props),
      reasonId: isUnrelease ? 0 : reasonId,
      occurredAt: occurredAtIso, // ISO oder null
      studentIds: studentIds
    });

    try {
      toast.add({
        severity: 'success',
        summary: isUnrelease
            ? `Entlassen zurückgenommen: ${studentIds.length}`
            : `Entlassen gespeichert: ${studentIds.length}`,
        life: 3000
      });
    } catch {
      /* noop */
    }

    // UI-Selektionen leeren
    for (const r of visible) {
      if (r?.selRelease) r.selRelease = false;
    }

    // Reason nur nach "Entlassen" zurücksetzen; bei Unrelease kann er stehen bleiben – wir setzen ihn aber konsistent zurück
    bulkReleaseReasonId.value = 0;

    await load();
    emit('updated');
  } catch (e) {
    const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Bulk-Entlassen fehlgeschlagen.';
    error.value = String(msg);
    try {
      toast.add({severity: 'warn', summary: 'Entlassen fehlgeschlagen', detail: String(msg), life: 6000});
    } catch {
      /* noop */
    }
  } finally {
    loading.value = false;
  }
}

function toggleFullscreen() {
  fullscreen.value = !fullscreen.value;
}

const props = defineProps({
  open: {type: Boolean, default: false},
  eventId: {type: [Number, String], default: null},
  eventTeacherFirstName: {type: String, default: null},
  eventTeacherLastName: {type: String, default: null},
  eventStatus: {type: String, default: null},
  eventTitle: {type: String, default: null},
  eventMandatory: {type: Boolean, default: false},
  eventStartsAt: {type: [String, Date, null], default: null},
  eventEndsAt: {type: [String, Date, null], default: null},
  // Neu: Vorfilter-Text (z. B. Schülername) beim Öffnen
  initialSearchTerm: {type: String, default: ''}
});
const emit = defineEmits(['close', 'updated', 'open-notification', 'open-notification-for-student']);

const loading = ref(false);
const error = ref('');
const rows = ref([]);
const toast = useToast();
const auth = useAuthStore();

// View-Modus: 'bulk' (Standard) | 'touch' (iPad)
const viewMode = ref('bulk');

function toggleViewMode() {
  viewMode.value = viewMode.value === 'bulk' ? 'touch' : 'bulk';
  // Bulk-Eingaben und Selektionen leeren, um Inkonsistenzen zu vermeiden
  try {
    bulkInValue.value = '';
    bulkOutValue.value = '';
    const list = rows.value || [];
    for (const r of list) {
      r.selIn = false;
      r.selOut = false;
      const blocks = Array.isArray(r.blocks) ? r.blocks : [];
      for (const b of blocks) {
        if (!b) continue;
        b.selIn = false;
        b.selOut = false;
      }
    }
  } catch {
    /* noop */
  }
}

function isReleaseDisabled(row) {
  if (!row) return true;

  // bestehende Sperren
  if (isReadOnly.value) return true;
  if (loading.value) return true;
  if (row.busy) return true;
  if (isBlocked(row.id)) return true;

  // NEU: sicherer Check laut Anforderung:
  // - arrived_at muss existieren
  // - released_at muss NULL sein (selber Tages-Stay)
  if (!row.schoolArrivedAtIso) return true;

  return row.schoolReleasedAtIso !== null && typeof row.schoolReleasedAtIso !== 'undefined';
}


// NEU: Release-Dialog State + Handler (fehlte → _ctx.openReleaseDialog ist dann undefined)
const releaseDialogOpen = ref(false);
const releaseDialogStudent = ref(null);
const releaseAtIso = computed(() => {
  const ymd = eventStartYmdBerlin.value;
  if (ymd?.y && ymd?.m && ymd?.d) {
    // ISO in UTC, aber auf "Event-Tag (Berlin)" fixiert – Zeit wird im Dialog ohnehin gewählt
    return berlinWallToIso(Number(ymd.y), Number(ymd.m), Number(ymd.d), 0, 0);
  }
  return new Date().toISOString();
});

function openReleaseDialog(row) {
  if (!row || isReleaseDisabled(row)) return;
  releaseDialogStudent.value = row;
  releaseDialogOpen.value = true;
}

async function onStudentReleased() {
  releaseDialogOpen.value = false;
  releaseDialogStudent.value = null;
  await load();
  emit('updated');
}

// NEU: Entfernen-Dialog State + Handler
const removeStudentConfirmOpen = ref(false);
const removeStudentRow = ref(null);
const removeStudentBusy = ref(false);

function openRemoveStudentConfirm(row) {
  if (!row) return;
  if (isReadOnly.value || loading.value || row.busy) return;
  removeStudentRow.value = row;
  removeStudentConfirmOpen.value = true;
}

function onRemoveStudentCancel() {
  if (removeStudentBusy.value) return;
  removeStudentConfirmOpen.value = false;
  removeStudentRow.value = null;
}

async function onRemoveStudentConfirm() {
  const r = removeStudentRow.value;
  if (!r || !props.eventId) return;

  removeStudentBusy.value = true;
  error.value = '';

  try {
    await api.delete(`/events/${props.eventId}/enrollments/${r.id}/cascade`);

    try {
      toast.add({severity: 'success', summary: 'Schüler entfernt.', life: 2500});
    } catch {
      /* noop */
    }

    removeStudentConfirmOpen.value = false;
    removeStudentRow.value = null;

    await load();
    emit('updated');
  } catch (e) {
    const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Entfernen fehlgeschlagen.';
    error.value = String(msg);
    try {
      toast.add({severity: 'warn', summary: 'Entfernen fehlgeschlagen', detail: String(msg), life: 6000});
    } catch {
      /* noop */
    }
  } finally {
    removeStudentBusy.value = false;
  }
}

// Touch-Zeitdialog State/Refs
const timeDialogOpen = ref(false);
const timeDialogBusy = ref(false);
const timeDialogError = ref('');
const timeDialogRow = ref(null);
const timeDialogBlockIndex = ref(0); // 0=root; >0 extra block
const timeDialogKind = ref('in'); // 'in' | 'out'
const timeHour = ref('');
const timeMinute = ref('');
const timeHourEl = ref(null);
const timeMinuteEl = ref(null);

const timeDialogTitle = computed(() => {
  const r = timeDialogRow.value;
  const who = r?.name ? ` — ${r.name}` : '';
  const what = timeDialogKind.value === 'in' ? 'Kommen' : 'Gehen';
  return `${what} setzen ${who}`;
});

// Bulk-States (Toolbar)
const bulkReleaseTimeValue = ref('');

watch(() => props.open, (v) => {
  if (v) {
    bulkInValue.value = '';
    bulkOutValue.value = '';
    bulkReleaseTimeValue.value = '';
  } else {
    bulkReleaseTimeValue.value = '';
  }
});

function onBulkReleaseTimeOpen() {
  // nur beim "Klick in das Time-Element" vorbefüllen, wenn das Feld noch leer ist
  const current = String(bulkReleaseTimeValue.value || '').trim();
  if (current) return;

  // 1) Wenn eine angehakte Entlassen-Zeile bereits eine Entlassen-Zeit hat -> diese übernehmen
  const list = filteredRows.value || [];
  for (const r of list) {
    if (!r?.selRelease) continue;
    if (isReleaseCheckboxDisabled(r)) continue;

    const label = String(r.schoolReleasedAtText || '').trim();
    if (label) {
      bulkReleaseTimeValue.value = label;
      return;
    }

    // Wenn kein Label vorhanden (noch nicht entlassen) -> jetzt befüllen
    bulkReleaseTimeValue.value = dateTimeMode.value ? nowBerlinYmdHi() : nowBerlinHHMM();
    return;
  }

  // 2) Fallback: falls selectedAnyRelease inkonsistent ist
  bulkReleaseTimeValue.value = dateTimeMode.value ? nowBerlinYmdHi() : nowBerlinHHMM();
}

function selectAllOnFocus(e) {
  try {
    const el = e?.target;
    if (el && typeof el.select === 'function') el.select();
  } catch {
    /* noop */
  }
}

function currentCellTimeText(row, blockIndex, kind) {
  if (!row) return '';
  if (blockIndex === 0) {
    return kind === 'in' ? (row.checkedInTime || '') : (row.checkedOutTime || '');
  }
  const b = row.blocks?.[blockIndex - 1];
  return kind === 'in' ? (b?.checkedInTime || '') : (b?.checkedOutTime || '');
}

function openTimeDialog(row, blockIndex, kind /* 'in' | 'out' */) {
  if (!row) return;
  timeDialogError.value = '';
  timeDialogRow.value = row;
  timeDialogBlockIndex.value = Number(blockIndex || 0);
  timeDialogKind.value = kind === 'out' ? 'out' : 'in';

  // Vorbelegung: vorhandene Zeit oder aktuelle Zeit (Berlin)
  const existing = currentCellTimeText(row, timeDialogBlockIndex.value, timeDialogKind.value);
  const seed = existing && /^\d{2}:\d{2}$/.test(existing) ? existing : nowBerlinHHMM();
  const [hh = '', mm = ''] = seed.split(':');
  timeHour.value = hh;
  timeMinute.value = mm;

  timeDialogOpen.value = true;

  nextTick(() => {
    try {
      if (timeHourEl.value) timeHourEl.value.focus();
    } catch {
      /* noop */
    }
  });
}

function closeTimeDialog() {
  if (timeDialogBusy.value) return;
  timeDialogOpen.value = false;
  timeDialogError.value = '';
  timeDialogRow.value = null;
  timeDialogBlockIndex.value = 0;
  timeDialogKind.value = 'in';
  timeHour.value = '';
  timeMinute.value = '';
}

function parseHHMMToIso(hh, mm) {
  const h = Number(String(hh ?? '').trim());
  const m = Number(String(mm ?? '').trim());
  if (!Number.isInteger(h) || !Number.isInteger(m)) return null;
  if (h < 0 || h > 23) return null;
  if (m < 0 || m > 59) return null;
  const hh2 = String(h).padStart(2, '0');
  const mm2 = String(m).padStart(2, '0');
  return toIsoFromInputValue(`${hh2}:${mm2}`, eventStartYmdBerlin.value || undefined);
}

async function clearTimeDialogValue() {
  const row = timeDialogRow.value;
  if (!row) return;

  timeDialogBusy.value = true;
  timeDialogError.value = '';
  let shouldClose = false;

  try {
    const blockIndex = Number(timeDialogBlockIndex.value || 0);
    const kind = timeDialogKind.value;

    if (blockIndex === 0) {
      // Root: "Kommen entfernen" → checkin_at = null (und sinnvollerweise auch checkout_at, damit kein "Gehen ohne Kommen" bleibt)
      if (kind === 'in') {
        const res = await updateAttendance(row, {checkin_at: null, checkout_at: null});
        if (!res?.ok) throw new Error(res?.error || 'Entfernen fehlgeschlagen.');

        // NEU: Nach Entfernen von "Kommen 1" sofort neu laden,
        // damit .inline-flex.last-out (Ankunft/letztes Gehen) sicher korrekt ist.
        await load();
      } else {
        const res = await updateAttendance(row, {checkout_at: null});
        if (!res?.ok) throw new Error(res?.error || 'Entfernen fehlgeschlagen.');
      }
    } else {
      // Extra-Block: über Bulk-Endpoint
      const entry = {
        studentId: Number(row.id),
        blockIndex: blockIndex,
        attendanceId: row.blocks?.[blockIndex - 1]?.attendanceId ?? null
      };
      if (kind === 'in') {
        entry.checkin_at = null;
        entry.checkout_at = null;
      } else {
        entry.checkout_at = null;
      }

      await api.post(`/events/${props.eventId}/attendances/extra/bulk`, {
        eventId: getBaseEventId(props),
        entries: [entry]
      });
      await load();
      emit('updated');
    }

    shouldClose = true;
  } catch (e) {
    timeDialogError.value = String(e?.message || 'Entfernen fehlgeschlagen.');
  } finally {
    timeDialogBusy.value = false;
    if (shouldClose) closeTimeDialog();
  }
}

async function saveTimeDialog() {
  const row = timeDialogRow.value;
  if (!row) return;

  timeDialogBusy.value = true;
  timeDialogError.value = '';
  let shouldClose = false;

  try {
    const iso = parseHHMMToIso(timeHour.value, timeMinute.value);
    if (!iso) {
      timeDialogError.value = 'Bitte eine gültige Zeit eingeben (HH 00–23, MM 00–59).';
      return;
    }

    const blockIndex = Number(timeDialogBlockIndex.value || 0);
    const kind = timeDialogKind.value;

    if (blockIndex === 0) {
      const payload = kind === 'in' ? {checkin_at: iso} : {checkout_at: iso};
      const res = await updateAttendance(row, payload);
      if (!res?.ok) throw new Error(res?.error || 'Speichern fehlgeschlagen.');

      // NEU: Nach "Kommen 1" sofort komplett neu laden,
      // damit .inline-flex.last-out (z.B. Ankunft/letztes Gehen) sicher aktuell ist.
      if (kind === 'in') {
        await load();
      }
    } else {
      const entry = {
        studentId: Number(row.id),
        blockIndex: blockIndex,
        attendanceId: row.blocks?.[blockIndex - 1]?.attendanceId ?? null
      };
      if (kind === 'in') entry.checkin_at = iso;
      else entry.checkout_at = iso;

      await api.post(`/events/${props.eventId}/attendances/extra/bulk`, {
        eventId: getBaseEventId(props),
        entries: [entry]
      });

      await load();
      emit('updated');
    }

    shouldClose = true;
  } catch (e) {
    const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Speichern fehlgeschlagen.';
    timeDialogError.value = String(msg);
  } finally {
    timeDialogBusy.value = false;
    if (shouldClose) closeTimeDialog();
  }
}

// Beim Schließen des Hauptdialogs auch den Zeitdialog schließen
watch(() => props.open, (v) => {
  if (!v) closeTimeDialog();
});

// NEU: Ankommen-Dialog Zustand
const arriveConfirmOpen = ref(false);
const arriveConfirmRow = ref(null);
const arriveConfirmBusy = ref(false);
const arriveConfirmSuggestedIso = ref(null);
const arriveConfirmTimeText = computed(() => {
  if (!arriveConfirmSuggestedIso.value) return '';
  return dateTimeMode.value
      ? isoToBerlinYmdHi(arriveConfirmSuggestedIso.value)
      : isoToBerlinHHMM(arriveConfirmSuggestedIso.value);
});

const status = ref(props.eventStatus);
const completeOpen = ref(false);
const notifViewOpen = ref(false);
const notifViewId = ref(null);
const mandatory = computed(() => !!props.eventMandatory);

const teacherName = computed(() => {
  const fn = (props.eventTeacherFirstName || '').trim();
  const ln = (props.eventTeacherLastName || '').trim();
  if (!fn && !ln) return '';
  return [fn, ln].filter(Boolean).join(' ');
});

// Admin darf Abschluss immer sehen; Lehrer nur bei 'started'
const isAdmin = computed(() => {
  const u = auth.user || {};
  const rid = u.roleId ?? u.role_id ?? 0;
  return rid === 1;
});
// Ab sofort: keine Readonly-Sperre mehr für Lehrkräfte bei abgeschlossenem Angebot
const isReadOnly = computed(() => false);
// Akzeptierte Notifications je Schüler-ID am Eventtag/-zeitraum
const acceptedNotifsMap = ref(new Map()); // studentId -> {subject, date_from, date_to}

// Hinweis: Inline-Meldungsdialog entfernt; NotificationViewDialog wird verwendet.

// Zusammenfassung/Zustände für Abschluss-Dialog
const searchTerm = ref('');
const presentFilter = ref('');   // '' | 'yes' | 'no'

const hasActiveFilters = computed(() => {
  return !!(
      String(searchTerm.value || '').trim() ||
      presentFilter.value
  );
});

const filteredRows = computed(() => {
  const term = String(searchTerm.value || '').trim().toLowerCase();
  const list = rows.value || [];
  return list.filter(r => {
    // Name
    if (term && !String(r.name || '').toLowerCase().includes(term)) return false;

    // Anwesend-Logik gemäß Vorgabe:
    // 'Ja': es gibt eine Kommen-Buchung und keine Gehen-Buchung (offener Block)
    // 'Nein': es gibt keine Kommen-Buchung ODER es gibt sowohl Kommen als auch Gehen (kein offener Block)
    const isCurrentlyPresent = (() => {
      // Root-Block offen?
      if (r.checkedInTime && !r.checkedOutTime) return true;
      // Falls Root abgeschlossen, offene Zusatzblöcke prüfen
      const blocks = Array.isArray(r.blocks) ? r.blocks : [];
      for (let i = blocks.length - 1; i >= 0; i--) {
        const b = blocks[i];
        if (b && b.checkedInTime && !b.checkedOutTime) return true;
      }
      return false;
    })();

    if (presentFilter.value === 'yes' && !isCurrentlyPresent) return false;
    if (presentFilter.value === 'no' && isCurrentlyPresent) return false;

    return true;
  });
});

// Aktiviert die Bulk-Speichern-Buttons, sobald mind. eine passende Checkbox selektiert ist
const selectedAnyIn = computed(() => {
  const list = filteredRows.value || [];
  for (const r of list) {
    if (r?.selIn) return true;
    const blocks = Array.isArray(r?.blocks) ? r.blocks : [];
    if (blocks.some(b => !!b?.selIn)) return true;
  }
  return false;
});

// Computed Property hinzufügen (nach eventTimesText):
const currentlyPresentCount = computed(() => {
  const list = rows.value || [];
  let count = 0;

  for (const row of list) {
    // Root-Block (Kommen 1):
    // checkin_at vorhanden UND checkout_at NICHT vorhanden
    if (row.checkedInTime && !row.checkedOutTime) {
      count++;
      continue; // nur einmal pro Schüler zählen
    }

    // Zusatzblöcke prüfen:
    // Wenn im Root bereits ein valides Checkout vorhanden ist,
    // dann einen Zusatzblock als "aktuell präsent" betrachten,
    // wenn Checkin vorhanden UND kein Checkout
    if (row.checkedOutTime && Array.isArray(row.blocks)) {
      // Suche den letzten Block mit Checkin aber ohne Checkout
      for (let i = row.blocks.length - 1; i >= 0; i--) {
        const block = row.blocks[i];
        if (block && block.checkedInTime && !block.checkedOutTime) {
          count++;
          break; // nur der letzte offene Block zählt
        }
      }
    }
  }

  return count;
});

const selectedAnyOut = computed(() => {
  const list = filteredRows.value || [];
  for (const r of list) {
    if (r?.selOut) return true;
    const blocks = Array.isArray(r?.blocks) ? r.blocks : [];
    if (blocks.some(b => !!b?.selOut)) return true;
  }
  return false;
});

// Dynamische Anzahl Anwesenheits-Blöcke (je 2 Spalten: Kommen/Gehen)
const attendanceBlocksCount = ref(1);

// Grid-Spalten (Header/Rows) dynamisch an Blockanzahl anpassen – identisch für Header und Body
// Basis: Schüler + je Block (Letztes Gehen, Kommen, Gehen)
const gridCols = computed(() => {
  const cols = ['300px']; // Schüler
  for (let i = 0; i < attendanceBlocksCount.value; i++) {
    cols.push('100px', '140px', '140px'); // je Block: Letztes Gehen, Kommen, Gehen
  }

  cols.push('minmax(100px, auto)');

  return cols.join(' ');
});

function resetFilters() {
  searchTerm.value = '';
  presentFilter.value = '';
}

// Garantiert frischer Mount nach Schließen > Öffnen (entfernt DOM-State zuverlässig)
const dialogKey = ref(0);

// Bulk-States (Toolbar)
const bulkInValue = ref('');
const bulkOutValue = ref('');

// Kandidaten: aktive Schüler, die (noch) nicht im Angebot sind
const candidateStudents = ref([]); // [{id, name}]
const selectedCandidateId = ref('');
const loadingCandidates = ref(false);

function normalizeStudentName(s) {
  if (!s) return '';
  const fn = s.firstName ?? s.first_name ?? s.firstname ?? s.givenName ?? '';
  const ln = s.lastName ?? s.last_name ?? s.lastname ?? s.familyName ?? '';
  const full = [fn, ln].filter(Boolean).join(' ').trim();
  return full || (s.name || '');
}

async function loadCandidates() {
  if (!props.open || !props.eventId) {
    candidateStudents.value = [];
    selectedCandidateId.value = '';
    return;
  }
  loadingCandidates.value = true;
  try {
    // 1) Primär: Kandidaten direkt für dieses Event (Backend filtert „nicht gebucht“)
    let data = [];
    try {
      const resCand = await api.get(`/events/${props.eventId}/candidates`);
      data = resCand?.data?.data?.items ?? resCand?.data?.data ?? resCand?.data ?? [];
    } catch {
      // 2) Fallback: Alle aktiven Schüler laden und clientseitig filtern
      const res = await api.get('/students', {params: {status: 'active'}});
      data = res?.data?.data?.items ?? res?.data?.data ?? res?.data ?? [];
      // Bereits gebuchte IDs ausschließen
      const booked = new Set((rows.value || []).map(r => Number(r.id)));
      data = (Array.isArray(data) ? data : []).filter(s => {
        const id = Number(s.id ?? s.student_id ?? 0);
        return id && !booked.has(id);
      });
    }

    const all = Array.isArray(data) ? data : [];
    candidateStudents.value = all
        .map(s => {
          const id = Number(s.id ?? s.student_id ?? 0);
          return id ? {id, name: normalizeStudentName(s)} : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    // Auswahl zurücksetzen, falls nicht mehr vorhanden
    if (!candidateStudents.value.some(c => String(c.id) === String(selectedCandidateId.value))) {
      selectedCandidateId.value = '';
    }
  } catch (e) {
    candidateStudents.value = [];
  } finally {
    loadingCandidates.value = false;
  }
}

async function addStudent() {
  if (isReadOnly.value) return;
  const sidRaw = selectedCandidateId.value;
  const sid = Number(sidRaw);
  if (!sid || !props.eventId) return;
  loading.value = true;
  try {
    // 1) Enrollment anlegen (robust mit Fallback auf Pfadvariante)
    try {
      await api.post(`/events/${props.eventId}/enrollments`, {studentId: sid});
    } catch {
      await api.post(`/events/${props.eventId}/enrollments/${sid}`);
    }

    // 2) Attendance anlegen (Check-in jetzt, Berlin)
    // const nowUi = dateTimeMode.value ? nowBerlinYmdHi() : nowBerlinHHMM();
    // const checkinIso = toIsoFromInputValue(nowUi, eventStartYmdBerlin.value || undefined);

    // Sonderfunktion: kurzen Slot (5 Minuten) mitspeichern, um Überschneidungen zu vermeiden.
    // Die Lehrkraft kann später die tatsächliche Gehen-Zeit nachpflegen.
    // let checkoutIsoShort = null;
    // try {
    //   const d = parseDbTsToDate(checkinIso) || (checkinIso ? new Date(checkinIso) : null);
    //   if (d && !Number.isNaN(d.getTime())) {
    //     checkoutIsoShort = new Date(d.getTime() + 5 * 60 * 1000).toISOString();
    //   }
    // } catch {
    //   checkoutIsoShort = null;
    // }

    // const payload = {
    //   status: 'expected',
    //   checkin_at: checkinIso
    // };
    // if (checkoutIsoShort) {
    //   payload.checkout_at = checkoutIsoShort;
    // }
    const payload = {
      status: 'expected',
    };
    await api.put(`/events/${props.eventId}/attendances/${sid}`, payload);

    toast.add({severity: 'success', summary: 'Schüler hinzugefügt.', life: 2500});
    // Neu laden
    await load();
    await loadCandidates();
    selectedCandidateId.value = '';
    emit('updated');
  } catch (e) {
    const statusCode = e?.response?.status ?? 0;
    const errData = e?.response?.data ?? {};
    const errRoot = errData?.error ?? errData ?? {};
    const errCode = (errRoot?.code ?? '').toString();

    if (statusCode === 409 && errCode === 'SCHOOL_STAY_MISSING') {
      // Stub-Row für den frisch eingeschriebenen Schüler vorbereiten (falls noch nicht vorhanden)
      let targetRow = (rows.value || []).find(r => String(r.id) === String(sid)) || null;
      const name = (candidateStudents.value.find(c => Number(c.id) === sid)?.name) || '';

      // Beabsichtigte Check-in-Zeit jetzt im Catch neu berechnen
      const nowUiCatch = dateTimeMode.value ? nowBerlinYmdHi() : nowBerlinHHMM();
      const intendedCheckin = toIsoFromInputValue(nowUiCatch, eventStartYmdBerlin.value || undefined);

      if (!targetRow) {
        targetRow = {
          id: String(sid),
          name,
          present: false,
          left: false,
          selIn: false,
          selOut: false,
          checkedInTime: '',
          checkedOutTime: '',
          blocks: [],
          busy: false,
          hasAttendance: false,
          _lastInSaved: null,
          _lastOutSaved: null,
          _lastInAttempt: intendedCheckin
        };
        rows.value.push(targetRow);
      } else {
        // Falls es bereits eine Zeile gibt, die intendierte Zeit merken
        targetRow._lastInAttempt = intendedCheckin;
      }

      const suggested = errRoot?.suggestedArrivedAt ?? errRoot?.suggested_arrived_at ?? null;
      arriveConfirmRow.value = targetRow;
      arriveConfirmSuggestedIso.value = suggested ? String(suggested) : null;
      arriveConfirmOpen.value = true;

      // Kein globaler Fehlertext setzen – der Dialog erklärt die Situation
      error.value = '';
      return;
    }

    error.value = 'Hinzufügen fehlgeschlagen.';
  } finally {
    loading.value = false;
  }
}

// Angebotszeiten (Titel): dd.mm. HH:ii - dd.mm. HH:ii bei mehrtägigen Angeboten, sonst HH:ii - HH:ii
const eventTimesText = computed(() => {
  const s = props.eventStartsAt;
  const e = props.eventEndsAt;
  if (!s || !e) return '';

  // Ohne TZ-Korrektur: Strings im lokalen 'YYYY-MM-DD HH:mm[:ss]'-Format direkt zerlegen,
  // Date-Werte nur formatieren.
  const parseLocal = (val) => {
    if (val instanceof Date && !Number.isNaN(val.getTime())) {
      const dm = new Intl.DateTimeFormat('de-DE', {timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit'});
      const hm = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const ymdFmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return {ddmm: dm.format(val), hm: hm.format(val), ymd: ymdFmt.format(val)};
    }
    const str = String(val).trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})(?::\d{2})?$/.exec(str);
    if (m) {
      const [, y, mo, d, hh, mi] = m;
      return {ddmm: `${d}.${mo}.`, hm: `${hh}:${mi}`, ymd: `${y}-${mo}-${d}`};
    }
    const d = new Date(str);
    if (!Number.isNaN(d.getTime())) {
      const dm = new Intl.DateTimeFormat('de-DE', {timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit'});
      const hm = new Intl.DateTimeFormat('de-DE', {
        timeZone: 'Europe/Berlin',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const ymdFmt = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Europe/Berlin',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      return {ddmm: dm.format(d), hm: hm.format(d), ymd: ymdFmt.format(d)};
    }
    return null;
  };

  const sp = parseLocal(s);
  const ep = parseLocal(e);
  if (!sp || !ep) return '';

  const sameDay = sp.ymd === ep.ymd;
  return sameDay ? `${sp.hm} - ${ep.hm}` : `${sp.ddmm} ${sp.hm} - ${ep.ddmm} ${ep.hm}`;
});

// DateTime-Modus je nach Event (tagesübergreifend)
const dateTimeMode = ref(false);
// Startdatum des Events in Berlin (für Time-only als Basis)
const eventStartYmdBerlin = ref(/** @type {{y:number,m:number,d:number}|null} */(null));
// Enddatum des Events in Berlin (für Datumsbereich)
const eventEndYmdBerlin = ref(/** @type {{y:number,m:number,d:number}|null} */(null));

// Ist der Event-Tag (Berlin) gleich „heute“ (Berlin)?
const isSameDayToday = computed(() => {
  const ev = eventStartYmdBerlin.value;
  const today = getBerlinYmdFromIso(new Date().toISOString());
  return !!(ev && today && ev.y === today.y && ev.m === today.m && ev.d === today.d);
});

// Unterdrückt Speichervorgänge während des initialen Ladevorgangs/Renderns
const suppressSave = ref(false);
// Sequenzzähler für Ladevorgänge, um veraltete Antworten zu ignorieren
let loadSeq = 0;
// Mehrfachtrigger zusammenfassen
let loadScheduled = false;

/**
 * Fasst mehrere watch()-Trigger in einem Tick zu genau 1 Load zusammen.
 * Lädt nur, wenn Dialog offen und eine eventId vorhanden ist.
 */
function scheduleLoad() {
  if (loadScheduled) return;
  if (!props.open || !props.eventId) return;
  loadScheduled = true;
  Promise.resolve().then(async () => {
    loadScheduled = false;
    await nextTick();
    // Falls beim Warten geschlossen/ID entfernt wurde: abbrechen
    if (!props.open || !props.eventId) return;
    await load();
  });
}

watch(() => props.open, (v) => {
  if (v) {
    status.value = props.eventStatus;
    // Bulk-Picker beim Öffnen zurücksetzen
    bulkInValue.value = '';
    bulkOutValue.value = '';
    bulkReleaseReasonId.value = 0;
    // Anwesenheitsfilter beim Öffnen auf "Alle" setzen
    presentFilter.value = '';
    // Vorfilter anwenden (z. B. Schülername aus der Schüler-Ansicht)
    if (typeof props.initialSearchTerm === 'string' && props.initialSearchTerm.trim().length > 0) {
      searchTerm.value = props.initialSearchTerm.trim();
    } else {
      searchTerm.value = '';
    }
    scheduleLoad();
    // Availability-Polling starten
    startAvailabilityPolling();

    // NEU: Entlass-Gründe fürs Dropdown laden
    loadReleaseReasons();
  } else {
    // Dialog geschlossen: Zustand leeren und laufende Loads invalidieren
    loadSeq++; // invalidiert in-flight Loads
    rows.value = [];
    error.value = '';
    completeOpen.value = false;
    fullscreen.value = false; // Vollbildmodus zurücksetzen
    dateTimeMode.value = false;
    eventStartYmdBerlin.value = null;
    dialogKey.value++; // harten Remount beim nächsten Öffnen erzwingen
    // Suchfilter zurücksetzen
    searchTerm.value = '';
    // Availability stoppen
    stopAvailabilityPolling();
  }
});
watch(() => props.eventId, () => {
  if (props.open) {
    // Bei Eventwechsel zuerst leeren, dann coalesced neu laden
    rows.value = [];
    error.value = '';
    selectedCandidateId.value = '';
    candidateStudents.value = [];
    scheduleLoad();
    // Polling neu starten
    stopAvailabilityPolling();
    startAvailabilityPolling();
  }
});
watch(() => props.eventStatus, (v) => {
  status.value = v;
});
// Zusätzlich: bei Änderung von Start/Ende neu berechnen (coalesced)
watch(() => [props.eventStartsAt, props.eventEndsAt], () => {
  if (props.open) scheduleLoad();
});

// Horizontaler Scroll-Sync: Listener setzen/entfernen wenn Ref wechselt
watch(attTable, (el, oldEl) => {
  try {
    if (oldEl) oldEl.removeEventListener('scroll', onTableScroll);
    if (el) {
      tableScrollLeft.value = el.scrollLeft || 0;
      el.addEventListener('scroll', onTableScroll, {passive: true});
    }
  } catch {
    /* noop */
  }
});

// Hinweis: canFinish steuert nur Sichtbarkeit, nicht Berechtigung im Backend
const canFinish = computed(() => {
  if (isAdmin.value) {
    return true;
  }
  return status.value === 'started';
});

// FlatPickr Zeit-Picker (wie im CreateEventDialog, hier time-only)
const timeConfig = {
  enableTime: true,
  noCalendar: true,
  dateFormat: 'H:i',
  time_24hr: true,
  allowInput: true,
  clickOpens: true,
  disableMobile: true,
  locale: German
};

// FlatPickr DateTime-Picker (für tagesübergreifend Angebote)
const dateTimeConfig = {
  enableTime: true,
  dateFormat: 'Y-m-d H:i',
  altInput: true,
  altFormat: 'd.m.Y H:i',
  time_24hr: true,
  allowInput: true,
  clickOpens: true,
  disableMobile: true,
  locale: German
};

// ---- Zeitzone Europe/Berlin – Helfer ----
function getTimeZoneOffset(timeZone, date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  });
  const parts = dtf.formatToParts(date);
  const map = {};
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;
  const asUTC = Date.UTC(+map.year, +map.month - 1, +map.day, +map.hour, +map.minute, +map.second);
  return asUTC - date.getTime(); // ms
}

function berlinWallToIso(year, month, day, hour, minute) {
  // Wandelt eine Berliner "Wanduhr"-Zeit in einen UTC-ISO-String
  const wallUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  const offset = getTimeZoneOffset('Europe/Berlin', new Date(wallUtc));
  return new Date(wallUtc - offset).toISOString();
}

// NEU: Robustes Parsen von DB-Zeitstrings
// - Enthält der String keine TZ (Z/+02:00/-0300) und passt auf 'Y-m-d H:i[:s]' oder 'Y-m-dTH:i[:s]',
//   dann als UTC interpretieren (damit 08:00 als 08:00Z verstanden wird).
function parseDbTsToDate(val) {
  if (!val) return null;
  if (val instanceof Date && !Number.isNaN(val.getTime())) return val;

  if (typeof val === 'string') {
    const s = val.trim();
    const hasTz = /(Z|[+-]\d{2}(?:\d{2})?)$/i.test(s);
    const naiveMatch = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(s);
    if (!hasTz && naiveMatch) {
      const [, y, m, d, hh, mm, ss] = naiveMatch;
      const ms = Date.UTC(+y, +m - 1, +d, +hh, +mm, +(ss ?? '0'));
      return new Date(ms);
    }
    // Fallback: normal parsen (ISO mit TZ oder sonstige Formate)
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

// Hilfsfunktion: Chronologie prüfen (Checkout >= Checkin)
function isOutAfterOrEqualIn(inIso, outIso) {
  if (!inIso || !outIso) return true;
  const inD = parseDbTsToDate(inIso);
  const outD = parseDbTsToDate(outIso);
  if (!inD || !outD) return true;
  return outD.getTime() >= inD.getTime();
}

function getBerlinYmdFromIso(iso) {
  const d = parseDbTsToDate(iso);
  if (!d) return null;
  const dtf = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin',
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
  const [{value: day}, , {value: month}, , {value: year}] = dtf.formatToParts(d);
  return {y: +year, m: +month, d: +day};
}

function isoToBerlinHHMM(iso) {
  const d = parseDbTsToDate(iso);
  if (!d) return '';
  const fmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return fmt.format(d);
}

// NEU: Anzeigeformat für tagübergreifende Anwesenheiten: "DD.MM.YYYY HH:MM"
function isoToBerlinDmyHi(iso) {
  const d = parseDbTsToDate(iso);
  if (!d) return '';
  return new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).format(d);
}

// ---- Meldungs-Zeitraum formatieren ----
function formatDateYmdToDe(ymd) {
  if (!ymd) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd).slice(0, 10));
  if (!m) return '';
  return `${m[3]}.${m[2]}.${m[1]}`;
}

function formatTimeToHHMM(t) {
  if (!t) return '';
  const s = String(t);
  const m = /^(\d{2}):(\d{2})/.exec(s);
  return m ? `${m[1]}:${m[2]}` : '';
}

function formatNotificationPeriod(hit) {
  if (!hit) return '';
  const kind = String(hit.kind || '').toLowerCase();
  const df = hit.date_from ? formatDateYmdToDe(String(hit.date_from)) : '';
  const dt = hit.date_to ? formatDateYmdToDe(String(hit.date_to)) : '';
  if (kind === 'time') {
    const t1 = formatTimeToHHMM(hit.time_from || '');
    const t2 = formatTimeToHHMM(hit.time_to || '');
    // Ein-Tages-Meldung mit Zeitfenster
    return [df, (t1 && t2) ? `${t1}–${t2}` : ''].filter(Boolean).join(' ');
  }
  // daily: ganztägig, ggf. mehrere Tage
  if (df && dt && df !== dt) {
    return `${df}–${dt}`;
  }
  return df || '';
}

// Meldungsdialog öffnen (NotificationViewDialog)
function openNotification(row) {
  const id = row?.notifyId ?? null;
  if (id) {
    notifViewId.value = id;
    notifViewOpen.value = true;
    // Kompatibilität: Event weiterreichen
    emit('open-notification', id);
  } else if (row?.id) {
    emit('open-notification-for-student', {studentId: Number(row.id)});
  }
}

// Meldungsdialog: beim Schließen/Statusänderung aus DB neu laden (volle Liste neu laden)
async function refreshNotificationBadges() {
  try {
    await load();
  } catch {
    // still
  }
}

async function onNotificationDialogClose() {
  notifViewOpen.value = false;
  await refreshNotificationBadges();
}

async function onNotificationDialogEvent() {
  // Accept/Reject/Read: Dialog wird in der Child-Komponente geschlossen → trotzdem sicherstellen
  notifViewOpen.value = false;
  await refreshNotificationBadges();
}

// Korrektur aus dem Viewer starten (Viewer vorher schließen)
const createOpen = ref(false);
const createNotifParentId = ref(null);
const createPrefill = ref({
  studentId: null,
  subject: '',
  dateFrom: null,
  dateTo: null,
  timeFrom: null,
  timeTo: null,
  kind: 'daily'
});

function buildPrefillFrom(item) {
  const root = item || {};
  const df = root.dateFrom ?? root.date_from ?? null;
  const dt = root.dateTo ?? root.date_to ?? null;
  const tf = root.timeFrom ?? root.time_from ?? null;
  const tt = root.timeTo ?? root.time_to ?? null;
  return {
    studentId: root.studentId ?? root.student_id ?? null,
    subject: root.subject ?? '',
    dateFrom: df || null,
    dateTo: dt || (tf || tt ? (df || null) : (dt || null)),
    timeFrom: tf || null,
    timeTo: tt || null,
    kind: (tf || tt) ? 'time' : 'daily'
  };
}

async function onNotifCreateCorrection(payload) {
  // Viewer schließen und DOM aktualisieren
  notifViewOpen.value = false;
  await nextTick();

  // Direkte Eltern-ID (aktuell betrachtete Meldung) verwenden
  const directId = payload?.id ?? payload?.notification_id ?? notifViewId.value ?? null;
  createNotifParentId.value = directId;

  // Prefill aus der aktuell betrachteten Meldung ableiten
  createPrefill.value = buildPrefillFrom(payload || {});
  createOpen.value = true;
}

function onCreateDialogClose() {
  createOpen.value = false;
}

async function onCreateCreated() {
  createOpen.value = false;
  await load();
  try {
    toast.add({severity: 'success', summary: 'Meldung gespeichert.', life: 3000});
  } catch {
    // ignored
  }
}

function isoToBerlinYmdHi(iso) {
  const d = parseDbTsToDate(iso);
  if (!d) return '';
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
  const parts = dtf.formatToParts(d);
  const map = {};
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}`;
}

function toIsoFromInputValue(value, fallbackYmd /* {y,m,d} */) {
  if (!value) return null;
  if (typeof value === 'string') {
    if (/^\d{2}:\d{2}$/.test(value)) {
      const [hh, mm] = value.split(':').map(Number);
      const base = fallbackYmd || getBerlinYmdFromIso(new Date().toISOString());
      if (!base) return null;
      return berlinWallToIso(base.y, base.m, base.d, hh, mm);
    }
    if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}$/.test(value)) {
      const [d, t] = value.split(' ');
      const [y, m, da] = d.split('-').map(Number);
      const [hh, mm] = t.split(':').map(Number);
      return berlinWallToIso(y, m, da, hh, mm);
    }
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/Berlin',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false
    });
    const parts = dtf.formatToParts(value);
    const map = {};
    for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;
    return berlinWallToIso(+map.year, +map.month, +map.day, +map.hour, +map.minute);
  }
  return null;
}

function nowBerlinHHMM() {
  const fmt = new Intl.DateTimeFormat('de-DE', {
    timeZone: 'Europe/Berlin',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
  return fmt.format(new Date());
}

function nowBerlinYmdHi() {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false
  });
  const parts = dtf.formatToParts(new Date());
  const map = {};
  for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day} ${map.hour}:${map.minute}`;
}

// Bei leerem Picker den aktuellen Zeitpunkt vorbelegen (Bulk-Inputs)
function prefillNowIfEmptyBulk(which /* 'in' | 'out' */) {
  const nowText = dateTimeMode.value ? nowBerlinYmdHi() : nowBerlinHHMM();
  if (which === 'in') {
    if (!bulkInValue.value) bulkInValue.value = nowText;
  } else if (which === 'out') {
    if (!bulkOutValue.value) bulkOutValue.value = nowText;
  }
}

// Zusätzlich: robustes Lesen von 'Y-m-d H:i' (naiv, lokal)
function extractYmdFromLocalString(s) {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})\s\d{2}:\d{2}$/.exec(s.trim());
  return m ? {y: +m[1], m: +m[2], d: +m[3]} : null;
}

// Neue Hilfsspalte: vorheriges Gehen pro Block (nur Fremdbuchungen; inkl. Fach)
// Anzeige: „letztes Gehen vor Kommen N“ → nur wenn eine Fremdbuchung vor dem jeweiligen Kommen liegt.
function prevOutText(row, bi) {
  if (!row || typeof bi !== 'number') return '';
  // Root-Block (Kommen 1): Fremdbuchung aus dem Backend (falls vorhanden)
  if (bi === 0) {
    const t0 = row.lastOutBeforeRoot || '';
    let s0 = row.lastOutSubjectRoot || '';
    s0 = s0.length > 17 ? (s0.slice(0, 17) + '...') : s0;

    // NEU: Fallback, wenn es kein "letztes Checkout" gibt:
    // dann Ankunft in der Schule (student_school_stays.arrived_at) anzeigen, sofern vorhanden.
    let arrived = row.schoolArrivedAtText || '';
    arrived = arrived ? `Ankunft -> ${arrived}` : '';

    if (!t0) return arrived;

    return s0 ? `${s0} -> ${t0}` : t0;
  }

  // Zusatzblöcke: Fremdbuchung im jeweiligen Zusatzblock (falls vorhanden)
  const blk = row.blocks?.[bi - 1];
  const t = blk?.lastOutBeforeBlock || '';
  let subj = blk?.lastOutSubject || '';
  subj = subj.length > 17 ? (subj.slice(0, 17) + '...') : subj;
  if (!t) return '';

  return subj ? `${subj} -> ${t}` : t;
}

// Kommen (zusätzliche Blöcke) nur erlauben, wenn der vorherige Block ein Gehen hat
function canInForBlock(row, bi) {
  if (!row || typeof bi !== 'number') return false;
  if (bi === 0) return true;
  if (bi === 1) {
    return !!row.checkedOutTime;
  }
  const prev = row.blocks?.[bi - 2];
  return !!(prev && prev.checkedOutTime);
}

// Prüfen, ob die Kommen-Checkbox im UI nicht disabled wäre (für Header-Toggles maßgeblich)
function isInCheckboxEnabled(row, blockIndex /* number */) {
  if (!row) return false;
  if (isReadOnly.value || row.busy) return false;
  if (blockIndex === 0) return true;
  return canInForBlock(row, blockIndex);
}

// Prüfen, ob die Gehen-Checkbox im UI nicht disabled wäre (für Header-Toggles maßgeblich)
function isOutCheckboxEnabled(row, blockIndex /* number */) {
  if (!row) return false;
  if (isReadOnly.value || row.busy) return false;
  if (blockIndex === 0) return !!row.present;
  const b = row.blocks?.[blockIndex - 1];
  return !!(b?.present);
}

// Prüfen, ob nach dem angegebenen Zusatzblock (idx) noch Buchungen vorhanden sind.
// idx ist der 0-basierte Index in row.blocks (entspricht block_index = idx + 1)
function hasLaterExtraBookings(row, idx /* number */) {
  if (!row || !Array.isArray(row.blocks)) return false;
  for (let j = idx + 1; j < row.blocks.length; j++) {
    const jb = row.blocks[j];
    if (!jb) continue;
    if (jb.present || jb.left || (jb.checkedInTime && jb.checkedInTime.trim()) || (jb.checkedOutTime && jb.checkedOutTime.trim())) {
      return true;
    }
  }
  return false;
}

// ---- Notifications (akzeptiert) laden und auf Rows anwenden ----
function ymdToString(ymd) {
  if (!ymd) return null;
  const y = String(ymd.y).padStart(4, '0');
  const m = String(ymd.m).padStart(2, '0');
  const d = String(ymd.d).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function overlapsYmdRanges(aFrom, aTo, bFrom, bTo) {
  // Alle Strings im Format YYYY-MM-DD, aTo/bTo dürfen null sein (interpretiert als aFrom/bFrom)
  const aF = aFrom;
  const aT = aTo || aFrom;
  const bF = bFrom;
  const bT = bTo || bFrom;
  if (!aF || !bF) return false;
  return !(aT < bF || bT < aF);
}

async function fetchAcceptedNotificationsForRows(dateFromYmd, dateToYmd) {
  try {
    const ids = (rows.value || []).map(r => Number(r.id)).filter(Boolean);
    if (!ids.length) {
      acceptedNotifsMap.value = new Map();
      return;
    }
    // Anfrage an Backend: akzeptierte Meldungen pro Schüler für Datumsbereich
    const res = await api.post('/notifications/by-students', {
      studentIds: ids,
      dateFrom: dateFromYmd,
      dateTo: dateToYmd || dateFromYmd
    });
    const items = res?.data?.data ?? res?.data ?? [];
    const map = new Map();
    for (const it of (Array.isArray(items) ? items : [])) {
      const sid = String(it.student_id ?? it.studentId ?? '');
      const subj = it.subject ?? '';
      const df = (it.date_from ?? it.dateFrom ?? null);
      const dt = (it.date_to ?? it.dateTo ?? null);
      const kind = (it.kind ?? it.type ?? null);
      const tf = it.time_from ?? it.timeFrom ?? null;
      const tt = it.time_to ?? it.timeTo ?? null;
      const nid = it.id ?? it.notification_id ?? null;
      // Falls Backend Datumsfilter nicht macht, lokal prüfen
      const ok = overlapsYmdRanges(
          dateFromYmd,
          dateToYmd || dateFromYmd,
          df ? String(df).slice(0, 10) : null,
          dt ? String(dt).slice(0, 10) : null
      );

      if (sid && ok) {
        // Pro Schüler reicht eine Meldung (die erste behalten)
        if (!map.has(sid)) {
          map.set(sid, {
            id: nid ?? null,
            subject: String(subj || '').trim(),
            date_from: df,
            date_to: dt,
            kind: kind ? String(kind) : null,
            time_from: tf,
            time_to: tt
          });
        }
      }
    }
    acceptedNotifsMap.value = map;
  } catch {
    acceptedNotifsMap.value = new Map();
  }
}

// Zustände für Übernahme-Bestätigung
const takeoverConfirmOpen = ref(false);
const takeoverRow = ref(null);
const takeoverBusy = ref(false);

// Verfügbarkeitseintrag zur aktuellen Übernahme
const takeoverAvail = computed(() => {
  const r = takeoverRow.value;
  if (!r) return null;
  return availabilityMap.value.get(String(r.id)) || availabilityMap.value.get(Number(r.id)) || null;
});

// Geplante Zeiten (ISO + Anzeige) für die Übernahme:
// - Datum immer Event-Tag (Europe/Berlin), Uhrzeit = aktuelle Berliner Uhrzeit
// - Checkout = Checkin minus 1 Minute (aber nicht vor blockierendem Checkin und nicht vor den Event-Tag zurück)
const takeoverPlanned = computed(() => {
  // Basisdatum (Event, Berlin)
  const base = eventStartYmdBerlin.value || getBerlinYmdFromIso(new Date().toISOString());
  // Aktuelle Berliner Zeit als 'Y-m-d HH:MM' → HH:MM herausziehen
  const nowText = nowBerlinYmdHi(); // Berlin
  const m = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})$/.exec(nowText);
  const hh = m ? parseInt(m[4], 10) : new Date().getHours();
  const mi = m ? parseInt(m[5], 10) : new Date().getMinutes();

  // Checkin-ISO: Eventdatum + aktuelle HH:MM (Berlin-Wanduhr → ISO)
  const checkinIso = base ? berlinWallToIso(base.y, base.m, base.d, hh, mi) : new Date().toISOString();

  // Checkout-Kandidat: 1 Minute vor Checkin
  const candOut = new Date(new Date(checkinIso).getTime() - 60_000);
  let checkoutIso = candOut.toISOString();

  // Nicht auf einen anderen Kalendertag (Berlin) zurückfallen lassen
  const candYmd = getBerlinYmdFromIso(checkoutIso);
  if (base && candYmd && (candYmd.y !== base.y || candYmd.m !== base.m || candYmd.d !== base.d)) {
    checkoutIso = checkinIso; // am Tagesanfang: auf Checkin klemmen
  }

  // Chronologie gegenüber blockierendem Checkin (falls vorhanden) sicherstellen
  const avail = takeoverAvail.value;
  const blockIn = avail?.since ? parseDbTsToDate(avail.since) : null;
  if (blockIn instanceof Date && !Number.isNaN(blockIn.getTime())) {
    const outD = parseDbTsToDate(checkoutIso);
    if (outD && outD.getTime() < blockIn.getTime()) {
      checkoutIso = checkinIso; // niemals vor dem blockierenden Checkin
    }
  }

  // Anzeige in Berlin
  const checkinTxt = dateTimeMode.value ? isoToBerlinYmdHi(checkinIso) : isoToBerlinHHMM(checkinIso);
  const checkoutTxt = dateTimeMode.value ? isoToBerlinYmdHi(checkoutIso) : isoToBerlinHHMM(checkoutIso);

  return {
    checkinIso,
    checkoutIso,
    checkinTxt,
    checkoutTxt
  };
});

// Öffnet den Bestätigungsdialog für eine Zeile
function openTakeoverConfirm(row) {
  if (!row) return;
  takeoverRow.value = row;
  takeoverConfirmOpen.value = true;
}

/* eslint-disable-next-line no-unused-expressions */
openTakeoverConfirm;

// Abbrechen
function onTakeoverCancel() {
  if (takeoverBusy.value) return;
  takeoverConfirmOpen.value = false;
  takeoverRow.value = null;
}

// Bestätigen
async function onTakeoverConfirm() {
  if (!takeoverRow.value) return;
  takeoverBusy.value = true;
  try {
    await performTakeOverForRow(takeoverRow.value);
    takeoverConfirmOpen.value = false;
    takeoverRow.value = null;
  } finally {
    takeoverBusy.value = false;
  }
}

/**
 * Führt die eigentliche Übernahme aus:
 * - Checkout im blockierenden Event auf geplanten Checkout (Eventdatum, HH:MM−1)
 * - Checkin im aktuellen Event auf geplanten Checkin (Eventdatum, HH:MM)
 * - Reload + Availability-Refresh
 */
async function performTakeOverForRow(row) {
  const avail = availabilityMap.value.get(String(row.id)) || availabilityMap.value.get(Number(row.id));
  if (!avail || avail.free !== false || !avail.blockingEventId) return;

  const studentId = Number(row.id);
  const blockingEventId = Number(avail.blockingEventId);

  // Geplante Zeiten aus takeoverPlanned verwenden
  const plan = takeoverPlanned.value || {};
  let nowIso = plan.checkinIso;
  let oneMinuteBeforeIso = plan.checkoutIso;

  // Fallback, falls plan nicht verfügbar ist
  if (!nowIso || !oneMinuteBeforeIso) {
    const base = eventStartYmdBerlin.value || getBerlinYmdFromIso(new Date().toISOString());
    const nowText = nowBerlinYmdHi();
    const m = /^(\d{4})-(\d{2})-(\d{2})\s(\d{2}):(\d{2})$/.exec(nowText);
    const hh = m ? parseInt(m[4], 10) : new Date().getHours();
    const mi = m ? parseInt(m[5], 10) : new Date().getMinutes();
    nowIso = base ? berlinWallToIso(base.y, base.m, base.d, hh, mi) : new Date().toISOString();
    oneMinuteBeforeIso = new Date(new Date(nowIso).getTime() - 60_000).toISOString();
  }

  row.busy = true;
  error.value = '';

  try {
    // Vorherige Attendance ermitteln
    const oldAttRes = await api.get(`/events/${blockingEventId}/attendances`);
    const oldItems = oldAttRes?.data?.data ?? oldAttRes?.data ?? [];
    const old = (Array.isArray(oldItems) ? oldItems : []).find(
        a => Number(a.student_id ?? a.studentId ?? a.student) === studentId
    );

    const oldStatus = old?.status || 'present';
    const oldCheckin = old?.checkin_at ?? old?.checkinAt ?? avail.since ?? null;
    if (!oldCheckin) {
      throw new Error('Vorheriges Kommen konnte nicht ermittelt werden; Übernahme nicht möglich.');
    }

    // 1) Checkout im blockierenden Event mit geplanter Zeit
    await api.put(`/events/${blockingEventId}/attendances/${studentId}`, {
      status: oldStatus,
      checkin_at: oldCheckin,
      checkout_at: oneMinuteBeforeIso
    });

    // 2) Checkin im aktuellen Event mit geplanter Zeit (nutzt updateAttendance inkl. Auto-Start)
    const resHere = await updateAttendance(row, {checkin_at: nowIso, checkout_at: null});
    if (!resHere?.ok) {
      throw new Error(resHere?.error || 'Kommen im aktuellen Angebot konnte nicht gesetzt werden.');
    }

    try {
      toast.add({severity: 'success', summary: 'Schüler übernommen.', life: 3000});
    } catch { /* noop */
    }

    // Refresh
    await load();
    await fetchAvailabilityOnce();
    emit('updated');
  } catch (e) {
    const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Übernahme fehlgeschlagen.';
    error.value = String(msg);
    try {
      toast.add({severity: 'warn', summary: 'Übernahme fehlgeschlagen', detail: String(msg), life: 6000});
    } catch { /* noop */
    }
  } finally {
    row.busy = false;
  }
}

// NEU: "Ankommen setzen" – Dialog-Handler
async function onArriveConfirmCancel() {
  if (arriveConfirmBusy.value) return;
  arriveConfirmOpen.value = false;
  arriveConfirmRow.value = null;
  arriveConfirmSuggestedIso.value = null;
}

async function onArriveConfirmConfirm() {
  const row = arriveConfirmRow.value;
  if (!row) return;

  arriveConfirmBusy.value = true;
  error.value = '';

  try {
    // Den zuletzt intendierten Check-in nochmals senden + Flag zum Stay-Anlegen
    const payload = {
      checkin_at: row._lastInAttempt ?? row._lastInSaved ?? null,
      createStayIfMissing: true
    };
    const res = await api.put(`/events/${props.eventId}/attendances/${row.id}`, payload);
    const updated = res?.data?.data ?? res?.data ?? {};

    const updInIso = updated.checkin_at ?? updated.checkinAt ?? null;
    const updOutIso = updated.checkout_at ?? updated.checkoutAt ?? null;

    row.present = !!updInIso;
    row.left = !!updOutIso;
    row.checkedInTime = updInIso
        ? (dateTimeMode.value ? isoToBerlinYmdHi(updInIso) : isoToBerlinHHMM(updInIso))
        : '';
    row.checkedOutTime = updOutIso
        ? (dateTimeMode.value ? isoToBerlinYmdHi(updOutIso) : isoToBerlinHHMM(updOutIso))
        : '';
    row._lastInSaved = updInIso ?? null;
    row._lastOutSaved = updOutIso ?? null;
    row.hasAttendance = true;

    arriveConfirmOpen.value = false;
    arriveConfirmRow.value = null;
    arriveConfirmSuggestedIso.value = null;

    // Angebot automatisch starten, wenn es noch nicht gestartet ist
    // (z.B. Status "ready")
    if (status.value !== 'started' && status.value !== 'completed' && status.value !== 'canceled') {
      try {
        await api.post(`/events/${props.eventId}/start`);
        status.value = 'started';
      } catch {
        // Fehler beim Starten hier bewusst ignorieren; Check-in bleibt gespeichert
      }
    }

    try {
      toast.add({
        severity: 'success',
        summary: 'Ankommen gesetzt.',
        detail: 'Der Schulaufenthalt wurde automatisch angelegt und der Check-in gespeichert.',
        life: 3000
      });
    } catch { /* noop */
    }

    emit('updated');
  } catch (e) {
    const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Speichern fehlgeschlagen.';
    error.value = String(msg);
    try {
      toast.add({severity: 'warn', summary: 'Ankommen konnte nicht gesetzt werden', detail: String(msg), life: 6000});
    } catch { /* noop */
    }
  } finally {
    arriveConfirmBusy.value = false;
  }
}

function headerToggleReleaseAll() {
  if (isReadOnly.value) return;
  const visible = filteredRows.value || [];

  for (const r of visible) {
    if (!r) continue;
    // Auch bereits entlassene berücksichtigen; nur echte Checkbox-Disable-Kriterien anwenden
    if (isReleaseCheckboxDisabled(r)) continue;

    r.selRelease = !r.selRelease;
  }
}

function isReleased(row) {
  return !!(row?.schoolReleasedAtIso);
}

async function load() {
  if (!props.eventId || !props.open) return;
  const seq = ++loadSeq; // Sequenz für diesen Load
  loading.value = true;
  error.value = '';
  suppressSave.value = true;
  try {
    const [enrRes, attRes, attExtraRes] = await Promise.all([
      api.get(`/events/${props.eventId}/enrollments`),
      api.get(`/events/${props.eventId}/attendances`),
      api.get(`/events/${props.eventId}/attendances/extra`)
    ]);
    // Falls inzwischen geschlossen/wechselte Event: Antwort ignorieren
    if (seq !== loadSeq || !props.open) {
      return;
    }
    const enrollmentsRaw = enrRes?.data?.data?.items ?? [];
    const attendancesRaw = attRes?.data?.data ?? [];
    const extrasRaw = attExtraRes?.data?.data ?? [];

    // Start/Ende kommen jetzt als Props aus der Liste
    const startsIso = props.eventStartsAt ?? null;
    const endsIso = props.eventEndsAt ?? null;

    // Primär: 'Y-m-d H:i' direkt lesen (naiv), sonst ISO→Berlin
    const startLocal = extractYmdFromLocalString(String(startsIso || ''));
    const endLocal = extractYmdFromLocalString(String(endsIso || ''));
    const startYmd = startLocal || (startsIso ? getBerlinYmdFromIso(startsIso) : null) || getBerlinYmdFromIso(new Date().toISOString());
    const endYmd = endLocal || (endsIso ? getBerlinYmdFromIso(endsIso) : null);

    eventStartYmdBerlin.value = startYmd || null;
    eventEndYmdBerlin.value = endYmd || startYmd || null;
    dateTimeMode.value = !!(startYmd && endYmd) && (
        startYmd.y !== endYmd.y || startYmd.m !== endYmd.m || startYmd.d !== endYmd.d
    );

    const attMap = {};
    for (const a of attendancesRaw) {
      attMap[String(a.student_id)] = a;
    }

    rows.value = (Array.isArray(enrollmentsRaw) ? enrollmentsRaw : []).map((p) => {
      const sid = String(p.student_id ?? p.id ?? p.student?.id ?? '');
      const s = p.student ?? p;
      const fn = s.firstName ?? s.first_name ?? s.firstname ?? '';
      const ln = s.lastName ?? s.last_name ?? s.lastname ?? '';

      const a = attMap[sid] ?? {};
      const present = Boolean(a.checkin_at);
      const left = Boolean(a.checkout_at);

      // Fremdbuchung vor Root-Kommen (nur Anzeige)
      const lastIsoRoot = a.last_out_before_checkin ?? a.lastOutBeforeCheckin ?? null;
      const lastOutBeforeRoot = lastIsoRoot
          ? (dateTimeMode.value ? isoToBerlinDmyHi(lastIsoRoot) : isoToBerlinHHMM(lastIsoRoot))
          : '';
      const lastOutSubjectRoot = a.last_out_subject ?? a.lastOutSubject ?? '';

      // NEU: Schul-Ankunft (Fallback-Anzeige, wenn kein letztes Checkout vorhanden)
      const schoolArrivedIso = a.school_arrived_at ?? a.schoolArrivedAt ?? null;
      const schoolReleasedIso = a.school_released_at ?? a.schoolReleasedAt ?? a.school_stay_released_at ?? a.schoolStayReleasedAt ?? null;
      const schoolArrivedAtText = schoolArrivedIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(schoolArrivedIso) : isoToBerlinHHMM(schoolArrivedIso))
          : '';
      const schoolReleasedAtText = schoolReleasedIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(schoolReleasedIso) : isoToBerlinHHMM(schoolReleasedIso))
          : '';

      let inTime = '';
      if (a.checkin_at) {
        inTime = dateTimeMode.value ? isoToBerlinDmyHi(a.checkin_at) : isoToBerlinHHMM(a.checkin_at);
      }
      let outTime = '';
      if (a.checkout_at) {
        outTime = dateTimeMode.value ? isoToBerlinDmyHi(a.checkout_at) : isoToBerlinHHMM(a.checkout_at);
      }

      return {
        id: sid,
        name: [fn, ln].filter(Boolean).join(' ') || (s.name || ''),
        absenceReason: s.absenceReason ?? s.absence_reason ?? '',
        latenessSeconds: a.lateness_seconds ?? a.latenessSeconds ?? 0,
        present,
        left,
        selIn: false,
        selOut: false,
        selRelease: false,
        checkedInTime: inTime,
        checkedOutTime: outTime,
        lastOutBeforeRoot,
        lastOutSubjectRoot,
        schoolArrivedAtIso: schoolArrivedIso ? String(schoolArrivedIso) : null,
        schoolReleasedAtIso: schoolReleasedIso ? String(schoolReleasedIso) : null,
        schoolArrivedAtText,
        schoolReleasedAtText,
        attendanceId: a.id ?? a.attendance_id ?? null,
        blocks: [],
        busy: false,
        hasAttendance: Boolean(attMap[sid]),
        _lastInSaved: a.checkin_at ? String(a.checkin_at) : null,
        _lastOutSaved: a.checkout_at ? String(a.checkout_at) : null,
        notifyAccepted: false,
        notifySubject: '',
        notifyPeriod: '',
        notifyId: null
      };
    });

    // Zusätzliche Blöcke des Events in die Rows mappen und Spaltenanzahl bestimmen
    let maxBlockIndex = 0;
    const rowsById = new Map((rows.value || []).map(r => [String(r.id), r]));

    for (const ex of (Array.isArray(extrasRaw) ? extrasRaw : [])) {
      const sid = String(ex.student_id ?? ex.studentId ?? '');
      const bix = Number(ex.block_index ?? ex.blockIndex ?? 0);
      if (!sid || !(bix >= 1)) continue;
      const r = rowsById.get(sid);
      if (!r) continue;
      // Zielindex im UI-Array
      const idx = bix - 1;
      if (!Array.isArray(r.blocks)) r.blocks = [];
      if (!r.blocks[idx]) {
        r.blocks[idx] = {
          present: false,
          left: false,
          selIn: false,
          selOut: false,
          checkedInTime: '',
          checkedOutTime: '',
          lastOutBeforeBlock: '',
          lastOutSubject: '',
          attendanceId: null
        };
      }
      const inIso = ex.checkin_at ?? ex.checkinAt ?? null;
      const outIso = ex.checkout_at ?? ex.checkoutAt ?? null;
      r.blocks[idx].present = !!inIso;
      r.blocks[idx].left = !!outIso;
      r.blocks[idx].attendanceId = ex.id ?? ex.attendance_id ?? ex.attendanceId ?? r.blocks[idx].attendanceId ?? null;
      r.blocks[idx].checkedInTime = inIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(inIso) : isoToBerlinHHMM(inIso))
          : '';
      r.blocks[idx].checkedOutTime = outIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(outIso) : isoToBerlinHHMM(outIso))
          : '';
      // Fremdbuchung: letztes Gehen vor Kommen (inkl. Fach)
      const lastIso = ex.last_out_before_checkin ?? ex.lastOutBeforeCheckin ?? null;
      r.blocks[idx].lastOutBeforeBlock = lastIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(lastIso) : isoToBerlinHHMM(lastIso))
          : '';
      r.blocks[idx].lastOutSubject = ex.last_out_subject ?? ex.lastOutSubject ?? '';
      if (bix > maxBlockIndex) maxBlockIndex = bix;
    }
    attendanceBlocksCount.value = Math.max(1, 1 + maxBlockIndex);

    // Akzeptierte Notifications für den Eventzeitraum laden und Rows markieren
    const fromYmd = ymdToString(eventStartYmdBerlin.value);
    const toYmd = ymdToString(eventEndYmdBerlin.value);
    await fetchAcceptedNotificationsForRows(fromYmd, toYmd);
    for (const r of rows.value) {
      const hit = acceptedNotifsMap.value.get(String(r.id));
      r.notifyAccepted = !!hit;
      r.notifySubject = hit?.subject || '';
      r.notifyPeriod = hit ? formatNotificationPeriod(hit) : '';
      r.notifyId = hit?.id ?? null;
      // Bei vorhandener akzeptierter Meldung UI-Interaktionen konsistent schließen
      if (r.notifyAccepted) {
        r.showRelease = false;
      }
    }

    // Kandidatenliste aktualisieren (aktive Schüler, die nicht gebucht sind)
    await loadCandidates();
  } catch (e) {
    error.value = 'Teilnehmer konnten nicht geladen werden.';
  } finally {
    await nextTick();
    suppressSave.value = false;
    loading.value = false;
  }
}

function isRowInOutSelected(row) {
  if (!row) return false;

  if (!!row.selIn || !!row.selOut) return true;

  const blocks = Array.isArray(row.blocks) ? row.blocks : [];
  for (const b of blocks) {
    if (!b) continue;
    if (!!b.selIn || !!b.selOut) return true;
  }

  return false;
}

function isRowReleaseSelected(row) {
  if (!row) return false;
  return !!row.selRelease;
}

async function updateAttendance(row, payload) {
  if (isReadOnly.value) return {ok: false, error: 'Schreibschutz aktiv.'};

  const providedIn = payload && Object.prototype.hasOwnProperty.call(payload, 'checkin_at');
  const providedOut = payload && Object.prototype.hasOwnProperty.call(payload, 'checkout_at');

  // Gegenwert immer aus zuletzt gespeicherten ISO-Werten ableiten,
  // damit keine Timepicker-Eingaben der jeweils anderen Richtung verwendet werden.
  const fullPayload = {
    checkin_at: providedIn
        ? payload.checkin_at
        : (row._lastInSaved ?? null),
    checkout_at: providedOut
        ? payload.checkout_at
        : (row._lastOutSaved ?? null)
  };

  // Plausibilitätsprüfung: Gehen darf nicht vor Kommen liegen
  if (fullPayload.checkin_at && fullPayload.checkout_at) {
    if (!isOutAfterOrEqualIn(fullPayload.checkin_at, fullPayload.checkout_at)) {
      return {ok: false, error: 'Gehen darf nicht vor Kommen liegen.'};
    }
  }

  row.busy = true;
  error.value = '';

  // Merke die zuletzt intendierte Check-in-Zeit für evtl. Bestätigungs-Flow (Stay anlegen)
  if (fullPayload.checkin_at) {
    row._lastInAttempt = fullPayload.checkin_at;
  }

  try {
    const res = await api.put(`/events/${props.eventId}/attendances/${row.id}`, fullPayload);
    row.hasAttendance = true;

    // *** WICHTIG: Ab hier ausschließlich den RESPONSE als Wahrheit verwenden ***
    const updated = res?.data?.data ?? res?.data ?? {};

    // Falls das Backend wegen Folgebuchungen automatisch eine kurze Gehen-Zeit
    // (5 Minuten nach Kommen) gesetzt hat, informieren wir den Nutzer.
    if (updated && updated.auto_short_checkout) {
      try {
        toast.add({
          severity: 'info',
          summary: 'Kurzer Anwesenheits-Slot gesetzt',
          detail: 'Für diesen Schüler bestehen an diesem Tag bereits weitere Buchungen. Es wurde automatisch eine kurze Gehen-Zeit 5 Minuten nach dem Kommen gesetzt. Bitte passen Sie die Zeit bei Bedarf an.',
          life: 15000
        });
      } catch {
        // Toast-Fehler ignorieren
      }
    }

    // Zeiten aus Response lesen (Server kann sie korrigiert haben)
    const updInIso = updated.checkin_at ?? updated.checkinAt ?? null;
    const updOutIso = updated.checkout_at ?? updated.checkoutAt ?? null;

    // Präsenz-/Gehen-Status rein aus der Response ableiten
    row.present = !!updInIso;
    row.left = !!updOutIso;

    // Zeiten, die im UI angezeigt werden (Spalte "Kommen"/"Gehen")
    row.checkedInTime = updInIso
        ? (dateTimeMode.value ? isoToBerlinDmyHi(updInIso) : isoToBerlinHHMM(updInIso))
        : '';

    row.checkedOutTime = updOutIso
        ? (dateTimeMode.value ? isoToBerlinDmyHi(updOutIso) : isoToBerlinHHMM(updOutIso))
        : '';

    // "Letzter gespeicherter Wert" ebenfalls aus dem Response ableiten
    row._lastInSaved = updInIso ?? null;
    row._lastOutSaved = updOutIso ?? null;

    // NEU: Nach dem Speichern Release-Check triggern (damit isReleaseDisabled() sofort neu bewertet wird)
    // 1) School-Stay-Infos aus Response übernehmen (falls vorhanden)
    const updSchoolArrivedIso =
        updated.school_arrived_at ?? updated.schoolArrivedAt ?? null;
    const updSchoolReleasedIso =
        updated.school_released_at ?? updated.schoolReleasedAt ?? updated.school_stay_released_at ?? updated.schoolStayReleasedAt ?? null;

    if (typeof updSchoolArrivedIso !== 'undefined') {
      row.schoolArrivedAtIso = updSchoolArrivedIso ? String(updSchoolArrivedIso) : null;
      row.schoolArrivedAtText = updSchoolArrivedIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(updSchoolArrivedIso) : isoToBerlinHHMM(updSchoolArrivedIso))
          : '';
    }
    if (typeof updSchoolReleasedIso !== 'undefined') {
      row.schoolReleasedAtIso = updSchoolReleasedIso ? String(updSchoolReleasedIso) : null;
      row.schoolReleasedAtText = updSchoolReleasedIso
          ? (dateTimeMode.value ? isoToBerlinDmyHi(updSchoolReleasedIso) : isoToBerlinHHMM(updSchoolReleasedIso))
          : '';
    }

    // 2) Blockierungs-/Availability-Status direkt aktualisieren (ohne 10s Poll-Wartezeit)
    try {
      await fetchAvailabilityOnce();
    } catch {
      /* noop */
    }

    // Event ggf. automatisch auf 'started' setzen
    if (status.value !== 'started' && status.value !== 'completed' && status.value !== 'canceled') {
      try {
        await api.post(`/events/${props.eventId}/start`);
        status.value = 'started';
      } catch {
        /* ignore */
      }
    }

    emit('updated');

    return {ok: true, result: updated};
  } catch (e) {
    // Spezieller Fall: BACKEND meldet fehlenden School-Stay (Dialog anbieten)
    const statusCode = e?.response?.status ?? 0;
    const errData = e?.response?.data ?? {};
    const errRoot = errData?.error ?? errData ?? {};
    const errCode = (errRoot?.code ?? '').toString();

    if (statusCode === 409 && errCode === 'SCHOOL_STAY_MISSING') {
      const suggested = errRoot?.suggestedArrivedAt ?? errRoot?.suggested_arrived_at ?? null;
      arriveConfirmRow.value = row;
      arriveConfirmSuggestedIso.value = suggested ? String(suggested) : null;
      arriveConfirmOpen.value = true;
      // Kein globaler Fehlertext – der Dialog erklärt die Situation
      error.value = '';
      return {ok: false, error: 'SCHOOL_STAY_MISSING'};
    }

    const msg =
        errRoot?.message ||
        e?.response?.data?.error?.message ||
        e?.response?.data?.error ||
        e?.message ||
        'Speichern fehlgeschlagen.';

    error.value = msg;
    return {ok: false, error: msg};
  } finally {
    row.busy = false;
  }
}

// Bulk-Aktionen
async function applyBulkIn() {
  if (isReadOnly.value) return;

  const uiValRaw = bulkInValue.value;
  const uiVal = typeof uiValRaw === 'string' ? uiValRaw.trim() : uiValRaw;
  let isDelete = !uiVal;

  // Nur ausgewählte und sichtbare Zeilen berücksichtigen
  const visible = filteredRows.value || [];

  // Root-Auswahl (Block 0)
  const rootCandidates = visible.filter(r => r.selIn && !r.busy && !isBlocked(r.id));

  // Zusatzblöcke sammeln
  const extraEntries = [];
  let appliedRoot = 0;
  let appliedExtra = 0;
  let skipped = 0;

  // Für Feedback-Sammlung
  const successItems = [];
  const errorItems = [];

  // Eingabe in ISO konvertieren; nicht parsbare Eingaben als "Löschen" interpretieren
  let inIso = null;
  if (!isDelete) {
    inIso = toIsoFromInputValue(uiVal, eventStartYmdBerlin.value || undefined);
    if (!inIso) {
      isDelete = true;
    }
  }

  if (!isDelete) {
    // Root verarbeiten
    for (const r of rootCandidates) {
      const outIso = r.left ? toIsoFromInputValue(r.checkedOutTime, eventStartYmdBerlin.value || undefined) : null;
      if (outIso && !isOutAfterOrEqualIn(inIso, outIso)) {
        skipped++;
        errorItems.push({name: r.name, block: 0, error: 'Chronologie: Kommen vor Gehen erforderlich.'});
        continue;
      }
      const res = await updateAttendance(r, {checkin_at: inIso});
      if (res?.ok) {
        // Response in updateAttendance hat den Row-State bereits aktualisiert
        r.selIn = false;
        appliedRoot++;
        successItems.push({name: r.name, block: 0});
      } else {
        errorItems.push({name: r.name, block: 0, error: res?.error || 'Fehler beim Speichern.'});
      }
    }

    // NEU: Nach erfolgreichem Root-"Kommen 1" einmal Reload,
    // damit .inline-flex.last-out (Ankunft/letztes Gehen) sofort korrekt ist.
    if (appliedRoot > 0) {
      await load();
    }

    // Zusatzblöcke: pro selektiertem Block Check-in setzen
    visible.forEach((r) => {
      if (r.busy || isBlocked(r.id)) return;
      const blocks = Array.isArray(r.blocks) ? r.blocks : [];
      blocks.forEach((b, idx) => {
        if (!b?.selIn) return;
        // Vorheriger Block muss Gehen besitzen (Checkbox wäre sonst disabled) – zur Sicherheit nochmals prüfen
        const prevOutTxt = idx === 0 ? (r.checkedOutTime || '') : (blocks[idx - 1]?.checkedOutTime || '');
        if (!prevOutTxt) {
          skipped++;
          errorItems.push({name: r.name, block: idx + 1, error: 'Vorheriges Gehen fehlt.'});
          return;
        }
        // Chronologie gegenüber eigenem evtl. vorhandenem Checkout prüfen
        const ownOutIso = b?.left ? toIsoFromInputValue(b.checkedOutTime, eventStartYmdBerlin.value || undefined) : null;
        if (ownOutIso && !isOutAfterOrEqualIn(inIso, ownOutIso)) {
          skipped++;
          errorItems.push({name: r.name, block: idx + 1, error: 'Chronologie: Kommen vor Gehen erforderlich.'});
          return;
        }
        // Nur geändertes Feld für Extras senden: hier nur checkin_at
        extraEntries.push({
          studentId: Number(r.id),
          blockIndex: idx + 1,
          checkin_at: inIso,
          attendanceId: b?.attendanceId ?? null
        });
      });
    });
  } else {
    // Löschpfad: Kommen (und vorhandenes Gehen) entfernen
    for (const r of rootCandidates) {
      const res = await updateAttendance(r, {checkin_at: null, checkout_at: null});
      if (res?.ok) {
        // Backend hat gelöscht; UI wird aus Response/Reload aktualisiert
        r.selIn = false;
        appliedRoot++;
        successItems.push({name: r.name, block: 0});
      } else {
        errorItems.push({name: r.name, block: 0, error: res?.error || 'Fehler beim Entfernen.'});
      }
    }

    // NEU: Nach erfolgreichem Root-Entfernen ebenfalls Reload für .last-out
    if (appliedRoot > 0) {
      await load();
    }

    // Zusatzblöcke löschen
    visible.forEach((r) => {
      if (r.busy || isBlocked(r.id)) return;
      const blocks = Array.isArray(r.blocks) ? r.blocks : [];
      blocks.forEach((b, idx) => {
        if (!b?.selIn) return;
        // Nur für Zusatzblöcke (block_index > 0)
        if (idx >= 0 && hasLaterExtraBookings(r, idx)) {
          skipped++;
          errorItems.push({
            name: r.name,
            block: idx + 1,
            error: 'Folgebuchungen vorhanden – bitte zuerst spätere Blöcke bearbeiten.'
          });
          return;
        }
        // Löschen von Kommen in Extras -> beide Felder leeren (Block zurücksetzen/entfernen)
        extraEntries.push({
          studentId: Number(r.id),
          blockIndex: idx + 1,
          checkin_at: null,
          checkout_at: null,
          attendanceId: b?.attendanceId ?? null
        });
      });
    });
  }

  if (extraEntries.length > 0) {
    try {
      const res = await api.post(`/events/${props.eventId}/attendances/extra/bulk`, {
        eventId: getBaseEventId(props),
        entries: extraEntries
      });
      // IDs und (falls vorhanden) Erfolg/Fehler aus Antwort übernehmen
      const data = res?.data?.data ?? res?.data ?? null;
      const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const resultLookup = new Map(); // key: "sid-bi" -> {success, id?, error?}
      if (list.length > 0) {
        for (const it of list) {
          const sid = String(it.studentId ?? it.student_id ?? '');
          const bi = Number(it.blockIndex ?? it.block_index ?? 0);
          if (!sid || !bi) continue;
          const key = `${sid}-${bi}`;
          const id = it.id ?? it.attendanceId ?? it.attendance_id ?? null;
          const success = typeof it.success !== 'undefined' ? !!it.success
              : (typeof it.ok !== 'undefined' ? !!it.ok : !(typeof it.error !== 'undefined' && it.error));
          const errMsg = it?.error?.message ?? it?.error ?? it?.message ?? null;
          resultLookup.set(key, {success, id, error: errMsg || null});
        }
      }

      // UI: nur Auswahl/IDs aktualisieren; Zeiten nicht lokal setzen
      visible.forEach((r) => {
        const blocks = Array.isArray(r.blocks) ? r.blocks : [];
        blocks.forEach((b, i) => {
          const idx = i + 1;
          if (!b?.selIn) return;
          const key = `${String(r.id)}-${idx}`;
          const resItem = resultLookup.size > 0 ? resultLookup.get(key) : {success: true};
          if (resItem?.success !== false) {
            b.selIn = false;
            if (resItem?.id) {
              b.attendanceId = resItem.id;
            }
            appliedExtra++;
            successItems.push({name: r.name, block: idx});
          } else {
            const msg = resItem?.error || (isDelete ? 'Entfernen fehlgeschlagen.' : 'Speichern fehlgeschlagen.');
            errorItems.push({name: r.name, block: idx, error: msg});
          }
        });
      });
      // Reale Zeiten vom Backend neu laden
      await load();
      emit('updated');
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.error || e?.message || ((isDelete ? 'Entfernen' : 'Setzen') + ' (Kommen) für zusätzliche Blöcke fehlgeschlagen.');
      error.value = String(msg);
      try {
        toast.add({
          severity: 'warn',
          summary: (isDelete ? 'Entfernen' : 'Setzen') + ' (Kommen) fehlgeschlagen',
          detail: String(msg),
          life: 6000
        });
      } catch { /* noop */
      }
    }
  }

  // Feedback anzeigen
  const totalApplied = appliedRoot + appliedExtra;
  if (totalApplied > 0) {
    const lines = successItems.slice(0, 10).map(it => `Block ${it.block + 1}: ${it.name}`);
    const more = successItems.length > 10 ? `\n… und ${successItems.length - 10} weitere` : '';
    toast.add({
      severity: 'success',
      summary: (isDelete ? 'Kommen entfernt' : 'Kommen gesetzt') + `: ${totalApplied}` + (skipped ? ` — übersprungen: ${skipped}` : ''),
      detail: lines.join('\n') + more,
      life: 4000
    });
  }
  if (errorItems.length > 0) {
    const lines = errorItems.slice(0, 10).map(it => `Block ${it.block + 1}: ${it.name}${it.error ? ' — ' + it.error : ''}`);
    const more = errorItems.length > 10 ? `\n… und ${errorItems.length - 10} weitere Fehler` : '';
    toast.add({
      severity: 'warn',
      summary: 'Fehler bei Kommen',
      detail: lines.join('\n') + more,
      life: 6000
    });
  }
}

function getBaseEventId(ev) {
  if (!ev || !ev.eventId) return null;
  const match = String(ev.eventId).match(/^(\d+)/); // "42-b1" -> ["42-b1", "42"]
  return match ? Number(match[1]) : null;
}

async function applyBulkOut() {
  if (isReadOnly.value) return;

  const uiValRaw = bulkOutValue.value;
  const uiVal = typeof uiValRaw === 'string' ? uiValRaw.trim() : uiValRaw;
  const isDelete = !uiVal;

  // Nur ausgewählte und sichtbare Zeilen berücksichtigen
  const visible = filteredRows.value || [];

  // Root-Auswahl (Block 0) – nur bereits Anwesende
  const rootCandidates = visible.filter(r => r.selOut && r.present && !r.busy);

  // Zusatzblöcke sammeln
  const extraEntries = [];
  let appliedRoot = 0;
  let appliedExtra = 0;
  let skipped = 0;

  // Für Feedback-Sammlung
  const successItems = [];
  const errorItems = [];

  if (!isDelete) {
    const outIso = toIsoFromInputValue(uiVal, eventStartYmdBerlin.value || undefined);
    if (!outIso) return;

    // Root verarbeiten
    for (const r of rootCandidates) {
      const inIso = toIsoFromInputValue(r.checkedInTime, eventStartYmdBerlin.value || undefined);
      if (inIso && !isOutAfterOrEqualIn(inIso, outIso)) {
        skipped++;
        errorItems.push({name: r.name, block: 0, error: 'Chronologie: Gehen nach Kommen.'});
        continue;
      }
      const res = await updateAttendance(r, {checkout_at: outIso});
      if (res?.ok) {
        // Response in updateAttendance hat den Row-State bereits aktualisiert
        r.selOut = false;
        appliedRoot++;
        successItems.push({name: r.name, block: 0});
      } else {
        errorItems.push({name: r.name, block: 0, error: res?.error || 'Fehler beim Speichern.'});
      }
    }

    // Zusatzblöcke: pro selektiertem Block Checkout setzen (nur wenn b.present)
    visible.forEach((r) => {
      if (r.busy) return;
      const blocks = Array.isArray(r.blocks) ? r.blocks : [];
      blocks.forEach((b, idx) => {
        if (!b?.selOut || !b?.present) return;
        const inIso = b?.present ? toIsoFromInputValue(b.checkedInTime, eventStartYmdBerlin.value || undefined) : null;
        if (inIso && !isOutAfterOrEqualIn(inIso, outIso)) {
          skipped++;
          errorItems.push({name: r.name, block: idx + 1, error: 'Chronologie: Gehen nach Kommen.'});
          return;
        }
        // Nur geändertes Feld für Extras senden: hier nur checkout_at
        extraEntries.push({
          studentId: Number(r.id),
          blockIndex: idx + 1,
          checkout_at: outIso,
          attendanceId: b?.attendanceId ?? null
        });
      });
    });
  } else {
    // Löschpfad: nur Gehen entfernen
    for (const r of rootCandidates) {
      const res = await updateAttendance(r, {checkout_at: null});
      if (res?.ok) {
        // Backend hat gelöscht; UI wird aus Response/Reload aktualisiert
        r.selOut = false;
        appliedRoot++;
        successItems.push({name: r.name, block: 0});
      } else {
        errorItems.push({name: r.name, block: 0, error: res?.error || 'Fehler beim Entfernen.'});
      }
    }
    // Zusatzblöcke löschen (nur checkout)
    visible.forEach((r) => {
      if (r.busy) return;
      const blocks = Array.isArray(r.blocks) ? r.blocks : [];
      blocks.forEach((b, idx) => {
        if (!b?.selOut || !b?.present) return;
        // Nur für Zusatzblöcke (block_index > 0): Prüfen, ob spätere Buchungen existieren
        if (idx >= 0 && hasLaterExtraBookings(r, idx)) {
          skipped++;
          errorItems.push({
            name: r.name,
            block: idx + 1,
            error: 'Folgebuchungen vorhanden – bitte zuerst spätere Blöcke bearbeiten.'
          });
          return;
        }
        // Löschen von Gehen in Extras -> nur checkout_at null senden
        extraEntries.push({
          studentId: Number(r.id),
          blockIndex: idx + 1,
          checkout_at: null,
          attendanceId: b?.attendanceId ?? null
        });
      });
    });
  }

  if (extraEntries.length > 0) {
    try {
      const res = await api.post(`/events/${props.eventId}/attendances/extra/bulk`, {
        eventId: getBaseEventId(props),
        entries: extraEntries
      });
      // IDs und (falls vorhanden) Erfolg/Fehler aus Antwort übernehmen
      const data = res?.data?.data ?? res?.data ?? null;
      const list = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      const resultLookup = new Map(); // key: "sid-bi" -> {success, id?, error?}
      if (list.length > 0) {
        for (const it of list) {
          const sid = String(it.studentId ?? it.student_id ?? '');
          const bi = Number(it.blockIndex ?? it.block_index ?? 0);
          if (!sid || !bi) continue;
          const key = `${sid}-${bi}`;
          const id = it.id ?? it.attendanceId ?? it.attendance_id ?? null;
          const success = typeof it.success !== 'undefined' ? !!it.success
              : (typeof it.ok !== 'undefined' ? !!it.ok : !(typeof it.error !== 'undefined' && it.error));
          const errMsg = it?.error?.message ?? it?.error ?? it?.message ?? null;
          resultLookup.set(key, {success, id, error: errMsg || null});
        }
      }

      // UI: nur Auswahl/IDs aktualisieren; Zeiten nicht lokal setzen
      visible.forEach((r) => {
        const blocks = Array.isArray(r.blocks) ? r.blocks : [];
        blocks.forEach((b, i) => {
          const idx = i + 1;
          if (!b?.selOut || !b?.present) return;
          const key = `${String(r.id)}-${idx}`;
          const resItem = resultLookup.size > 0 ? resultLookup.get(key) : {success: true};
          if (resItem?.success !== false) {
            b.selOut = false;
            if (resItem?.id) {
              b.attendanceId = resItem.id;
            }
            appliedExtra++;
            successItems.push({name: r.name, block: idx});
          } else {
            const msg = resItem?.error || (isDelete ? 'Entfernen fehlgeschlagen.' : 'Speichern fehlgeschlagen.');
            errorItems.push({name: r.name, block: idx, error: msg});
          }
        });
      });
      // Reale Zeiten vom Backend neu laden
      await load();
      emit('updated');
    } catch (e) {
      const msg = e?.response?.data?.error?.message || e?.response?.data?.error || e?.message || ((isDelete ? 'Entfernen' : 'Setzen') + ' (Gehen) für zusätzliche Blöcke fehlgeschlagen.');
      error.value = String(msg);
      try {
        toast.add({
          severity: 'warn',
          summary: (isDelete ? 'Entfernen' : 'Setzen') + ' (Gehen) fehlgeschlagen',
          detail: String(msg),
          life: 6000
        });
      } catch { /* noop */
      }
    }
  }

  // Feedback anzeigen
  const totalApplied = appliedRoot + appliedExtra;
  if (totalApplied > 0) {
    const lines = successItems.slice(0, 10).map(it => `Block ${it.block + 1}: ${it.name}`);
    const more = successItems.length > 10 ? `\n… und ${successItems.length - 10} weitere` : '';
    toast.add({
      severity: 'success',
      summary: (isDelete ? 'Gehen entfernt' : 'Gehen gesetzt') + `: ${totalApplied}` + (skipped ? ` — übersprungen: ${skipped}` : ''),
      detail: lines.join('\n') + more,
      life: 4000
    });
  }
  if (errorItems.length > 0) {
    const lines = errorItems.slice(0, 10).map(it => `Block ${it.block + 1}: ${it.name}${it.error ? ' — ' + it.error : ''}`);
    const more = errorItems.length > 10 ? `\n… und ${errorItems.length - 10} weitere Fehler` : '';
    toast.add({
      severity: 'warn',
      summary: 'Fehler bei Gehen',
      detail: lines.join('\n') + more,
      life: 6000
    });
  }
}

async function applyBulkInClear() {
  if (isReadOnly.value) return;
  // Nur das Bulk-Eingabefeld leeren, keine gespeicherten Zeiten anrühren
  bulkInValue.value = '';
}

async function applyBulkOutClear() {
  if (isReadOnly.value) return;
  // Nur das Bulk-Eingabefeld leeren, keine gespeicherten Zeiten anrühren
  bulkOutValue.value = '';
}

async function applyBulkReleaseTimeClear() {
  if (isReadOnly.value) return;
  // Nur das Bulk-Eingabefeld leeren, keine gespeicherten Zeiten anrühren
  bulkReleaseTimeValue.value = '';
}

// Helper: Auswahl in allen anderen Blöcken abwählen (exklusive blockIndex)
function clearSelectionsExcept(blockIndex /* number */) {
  const visible = filteredRows.value || [];
  for (const r of visible) {
    if (r.busy) continue;
    // Block 0 (Root) nur behalten, wenn er der aktive Block ist
    if (blockIndex !== 0) {
      r.selIn = false;
      r.selOut = false;
    }
    // Zusätzliche Blöcke
    const blocks = Array.isArray(r.blocks) ? r.blocks : [];
    for (let i = 0; i < blocks.length; i++) {
      const bi = i + 1; // zusätzliche Blöcke beginnen bei Index 1
      if (bi !== blockIndex) {
        blocks[i].selIn = false;
        blocks[i].selOut = false;
      }
    }
  }
}

// Zeilen-Helfer: alle Kommen-Auswahlen außer einem Block in einer Zeile abwählen
function clearRowInSelections(row, exceptBlockIndex /* number */) {
  if (!row) return;
  if (exceptBlockIndex !== 0) {
    row.selIn = false;
  }
  const blocks = Array.isArray(row.blocks) ? row.blocks : [];
  for (let i = 0; i < blocks.length; i++) {
    const bi = i + 1;
    if (bi !== exceptBlockIndex && blocks[i]) {
      blocks[i].selIn = false;
    }
  }
}

// NEU: Zeilen-Helfer: alle Gehen-Auswahlen außer einem Block in einer Zeile abwählen
function clearRowOutSelections(row, exceptBlockIndex /* number */) {
  if (!row) return;
  if (exceptBlockIndex !== 0) {
    row.selOut = false;
  }
  const blocks = Array.isArray(row.blocks) ? row.blocks : [];
  for (let i = 0; i < blocks.length; i++) {
    const bi = i + 1;
    if (bi !== exceptBlockIndex && blocks[i]) {
      blocks[i].selOut = false;
    }
  }
}

// Change-Handler: Row-exklusives Umschalten für Kommen
function onSelectIn(row, blockIndex /* number */) {
  try {
    const selected = blockIndex === 0 ? !!row?.selIn : !!(row?.blocks?.[blockIndex - 1]?.selIn);
    if (selected) {
      clearRowInSelections(row, blockIndex);
      // Zeit für den Bulk-"Kommen"-Picker vorbelegen, falls noch leer
      if (!bulkInValue.value) {
        let timeText = '';
        if (blockIndex === 0) {
          timeText = row.checkedInTime || '';
        } else {
          const blk = row.blocks?.[blockIndex - 1];
          timeText = blk?.checkedInTime || '';
        }
        if (timeText) {
          bulkInValue.value = timeText;
        }
      }
    }
  } catch {
    /* noop */
  }
}

// Change-Handler: Umschalten für Gehen (nicht exklusiv); optional Bulk-"Gehen" vorbefüllen
function onSelectOut(row, blockIndex /* number */) {
  try {
    const selected = blockIndex === 0 ? !!row?.selOut : !!(row?.blocks?.[blockIndex - 1]?.selOut);
    if (selected) {
      // NEU: in der Zeile nur einen Gehen-Block aktiv lassen
      clearRowOutSelections(row, blockIndex);

      // Bulk-"Gehen" vorbefüllen, falls noch leer
      if (!bulkOutValue.value) {
        let timeText = '';
        if (blockIndex === 0) {
          timeText = row.checkedOutTime || '';
        } else {
          const blk = row.blocks?.[blockIndex - 1];
          timeText = blk?.checkedOutTime || '';
        }
        if (timeText) {
          bulkOutValue.value = timeText;
        }
      }
    }
  } catch {
    /* noop */
  }
}

// Header-Aktionen: Auswahl umschalten je nach vorhandener Zeit – blockbasiert
function headerTogglePresentByHasTimeIdx(blockIndex /* number */, hasTime /* boolean */) {
  if (isReadOnly.value) return;
  clearSelectionsExcept(blockIndex);
  const visible = filteredRows.value || [];
  for (const r of visible) {
    if (isBlocked(r.id)) continue;
    // Nur toggeln, wenn die entsprechende Checkbox (je Block) überhaupt erlaubt wäre
    if (!isInCheckboxEnabled(r, blockIndex)) continue;

    if (blockIndex === 0) {
      const hasIn = !!r.checkedInTime;
      if (hasTime ? hasIn : !hasIn) r.selIn = !r.selIn;
    } else {
      const b = r.blocks?.[blockIndex - 1];
      const hasIn = !!(b?.checkedInTime);
      if (b && (hasTime ? hasIn : !hasIn)) b.selIn = !b.selIn;
    }
  }
  // Bulk-"Kommen" vorbelegen, falls leer und eine Auswahl aktiv ist
  if (!bulkInValue.value) {
    let timeText = '';
    for (const r of visible) {
      if (blockIndex === 0) {
        if (r.selIn && r.checkedInTime) {
          timeText = r.checkedInTime;
          break;
        }
      } else {
        const b = r.blocks?.[blockIndex - 1];
        if (b?.selIn && b?.checkedInTime) {
          timeText = b.checkedInTime;
          break;
        }
      }
    }
    if (timeText) bulkInValue.value = timeText;
  }
}

function headerToggleLeftByHasTimeIdx(blockIndex /* number */, hasTime /* boolean */) {
  if (isReadOnly.value) return;
  clearSelectionsExcept(blockIndex);
  const visible = filteredRows.value || [];
  for (const r of visible) {
    if (!isOutCheckboxEnabled(r, blockIndex)) continue;

    if (blockIndex === 0) {
      const hasOut = !!r.checkedOutTime;
      if (hasTime ? hasOut : !hasOut) r.selOut = !r.selOut;
    } else {
      const b = r.blocks?.[blockIndex - 1];
      const hasOut = !!(b?.checkedOutTime);
      if (b && (hasTime ? hasOut : !hasOut)) b.selOut = !b.selOut;
    }
  }
  // Bulk-"Gehen" vorbelegen, falls leer und eine Auswahl aktiv ist
  if (!bulkOutValue.value) {
    let timeText = '';
    for (const r of visible) {
      if (blockIndex === 0) {
        if (r.selOut && r.checkedOutTime) {
          timeText = r.checkedOutTime;
          break;
        }
      } else {
        const b = r.blocks?.[blockIndex - 1];
        if (b?.selOut && b?.checkedOutTime) {
          timeText = b.checkedOutTime;
          break;
        }
      }
    }
    if (timeText) bulkOutValue.value = timeText;
  }
}

// Header-Aktionen: Auswahl aller sichtbaren invertieren – blockbasiert
function headerTogglePresentAllIdx(blockIndex /* number */) {
  if (isReadOnly.value) return;
  clearSelectionsExcept(blockIndex);
  const visible = filteredRows.value || [];
  for (const r of visible) {
    if (isBlocked(r.id)) continue;
    if (!isInCheckboxEnabled(r, blockIndex)) continue;

    if (blockIndex === 0) {
      r.selIn = !r.selIn;
    } else if (r.blocks?.[blockIndex - 1]) {
      r.blocks[blockIndex - 1].selIn = !r.blocks[blockIndex - 1].selIn;
    }
  }
  // Bulk-"Kommen" vorbelegen, falls leer und eine Auswahl aktiv ist
  if (!bulkInValue.value) {
    let timeText = '';
    for (const r of visible) {
      if (blockIndex === 0) {
        if (r.selIn && r.checkedInTime) {
          timeText = r.checkedInTime;
          break;
        }
      } else {
        const b = r.blocks?.[blockIndex - 1];
        if (b?.selIn && b?.checkedInTime) {
          timeText = b.checkedInTime;
          break;
        }
      }
    }
    if (timeText) bulkInValue.value = timeText;
  }
}

function headerToggleLeftAllIdx(blockIndex /* number */) {
  if (isReadOnly.value) return;
  clearSelectionsExcept(blockIndex);
  const visible = filteredRows.value || [];
  for (const r of visible) {
    if (!isOutCheckboxEnabled(r, blockIndex)) continue;

    if (blockIndex === 0) {
      r.selOut = !r.selOut;
    } else {
      const b = r.blocks?.[blockIndex - 1];
      if (b) b.selOut = !b.selOut;
    }
  }
  // Bulk-"Gehen" vorbelegen, falls leer und eine Auswahl aktiv ist
  if (!bulkOutValue.value) {
    let timeText = '';
    for (const r of visible) {
      if (blockIndex === 0) {
        if (r.selOut && r.checkedOutTime) {
          timeText = r.checkedOutTime;
          break;
        }
      } else {
        const b = r.blocks?.[blockIndex - 1];
        if (b?.selOut && b?.checkedOutTime) {
          timeText = b.checkedOutTime;
          break;
        }
      }
    }
    if (timeText) bulkOutValue.value = timeText;
  }
}

// Global: neuen Anwesenheitsblock (2 Spalten) für alle hinzufügen
function addBlockForAll() {
  if (isReadOnly.value) return;
  attendanceBlocksCount.value = Math.max(1, Number(attendanceBlocksCount.value) + 1);
  const list = rows.value || [];
  for (const r of list) {
    if (!Array.isArray(r.blocks)) r.blocks = [];
    r.blocks.push({
      present: false,
      left: false,
      selIn: false,
      selOut: false,
      checkedInTime: '',
      checkedOutTime: '',
      lastOutBeforeBlock: '',
      lastOutSubject: ''
    });
  }
}

// Bestehende Funktionen bleiben erhalten; die direkten Save-on-Update Varianten werden nicht mehr verwendet
// function saveInTime(...) { ... } und function saveOutTime(...) { ... } werden nicht mehr aufgerufen.


async function openCompleteConfirm() {
  loading.value = false;
  completeOpen.value = true;
}

function onCompletedFromCompleteDialog() {
  completeOpen.value = false;
  status.value = 'completed';
  // UI aktualisieren und Eltern informieren
  toast.add({severity: 'success', summary: 'Angebot beendet.', life: 3000});
  load();
  emit('updated');
}
</script>

<style scoped>
/* Toolbar/Layout */
.toolbar {
  display: flex;
  gap: 8px;
  align-items: baseline;
  position: relative;
  padding-top: 5px;
}

.toolbar-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.toolbar-actions.touch-view {
  position: absolute;
  top: 10px;
  right: 0;
}

.add-student {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  max-width: 335px;
  margin-right: 70px;
}

/* Vollbild-Layer: füllt den gesamten Viewport und liegt über allem */
.as-fullscreen {
  position: fixed;
  inset: 0;
  left: 0;
  top: 0;
  width: 100vw;
  height: 100vh;
  z-index: 100;
  display: grid;
  align-items: stretch;
}

/* Falls der Dialog eine innere Container-Klasse nutzt, strecken und Rundungen entfernen */
.as-fullscreen :deep(.dialog) {
  width: 100%;
  height: 100%;
  max-width: 100vw;
  max-height: 100vh;
  margin: 0;
  border-radius: 0;
}

/* Inhalt im Vollbild nutzen */
.as-fullscreen .att-table {
  height: 90%;
}

.as-fullscreen .att-body {
  max-height: calc(100vh - 140px);
}

.bulk-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  width: auto;
  flex: 1 1 auto;
}

.bulk-actions .bulk-actions-item {
  display: flex;
  flex-direction: column;
  align-items: baseline;
  gap: 2px;
}

.bulk-actions .bulk-actions-item .actions-item {
  gap: 6px;
  display: inline-flex;
  align-items: center;
}

.bulk-actions input.flatpickr-datetime {
  width: 55px !important;
  text-align: center;
}

.bulk-actions input.flatpickr-datetime::placeholder {
  font-size: 13px;
}

/* Inhalt rechtsbündig innerhalb des „vollen“ Items */
.bulk-actions-item.release-actions-items .bulk-actions-item-inner {
  display: flex;
  flex-direction: column;
}

/* Tabellenkopf fix, Body scrollt */
.att-table {
  display: grid;
  grid-template-rows: auto auto 1fr; /* Filter, Header, Body */
  gap: 8px;
  border-bottom: 1px solid var(--border);
  overflow-x: auto; /* horizontales Scrollen bei vielen Blöcken */
}

.small {
  font-size: 12px;
  line-height: 1.4;
}

.sep {
  margin-left: 12px;
}

.att-filters {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 5px;
  padding: 5px;
  background: whitesmoke;
  margin-top: -10px;
}

.att-header {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 4px 0 2px 0;
  border-bottom: 1px solid var(--border);
  /* Reihenfolge wird per Inline-Style (gridTemplateColumns) gesetzt */
  position: sticky;
  top: 0; /* bleibt am oberen Rand des Body-Containers, falls body overflowt */
  background: var(--surface);
  z-index: 1;
  width: 100%; /* passt sich der Spaltenanzahl an */
}

.att-header .col-head {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.att-header .col-tools {
  display: inline-flex;
  gap: 6px;
  justify-content: flex-start;
}

.att-header .col-tools .icon-btn {
  height: 28px;
  width: 28px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--gray-50);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.att-header .col-tools .icon-btn:disabled {
  opacity: .6;
  cursor: default;
}

.inline .time-text {
  min-width: 80px;
}

/* Name + Hinweis untereinander */
.name-line {
  font-weight: 600;
  line-height: 1.35;
}

.name-hint {
  margin-top: 4px;
}

/* Sticky: erste Spalte (Schüler) fixieren */
.att-header .sticky-col.sticky-name {
  position: sticky;
  left: 2px;
  z-index: 5;
  background: var(--surface);
  border-right: 1px solid var(--border);
  height: 100%;
  line-height: 48px;
  font-weight: 600;
}

.att-row > .sticky-col.sticky-name {
  position: sticky;
  left: 0;
  z-index: 4;
  background: var(--surface);
  border-right: 1px solid var(--border);
  height: 100%;
  padding-right: 44px;
}

/* NEU: Entfernen-Button rechtsbündig innerhalb der sticky-name Zelle */
.remove-student-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);

  height: 28px;
  width: 28px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--surface);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 0; /* überschreibt evtl. alte Rule */
}

.remove-student-btn:disabled {
  opacity: .6;
  cursor: default;
}

.att-body {
  overflow-y: auto;
  overflow-x: clip; /* horizontal clippen: keine zweite Scrollleiste und kein eigener Scroll-Container für X */
  max-height: 56vh; /* Standard-Dialoghöhe: nur Body scrollbar */
  padding-bottom: 2px;
}

.att-body.fullscreen {
  max-height: 78vh; /* Größerer nutzbarer Bereich im Vollbild */
}

.att-row {
  display: grid;
  gap: 8px;
  align-items: center;
  padding: 4px 0 2px 2px;
  border-bottom: 1px solid var(--border);
  grid-template-columns: 1.8fr 220px 220px 100px 250px 100px;
}

.att-row .att-row-actions-right {
  justify-content: flex-end;
}

.att-row .last-out .time-text {
  text-align: right;
  width: 100%;
  font-weight: 400;
  font-size: 11px;
  color: #444;
  line-height: 12px;
}

.att-empty {
  padding: 8px 0;
  margin: 0;
}

/* Titel-Aktion (Vollbild-Button) – direkt links neben dem Close-Button positioniert */
.title-expand {
  position: absolute;
  right: 120px;
  top: 50%;
  transform: translateY(-50%);
  height: 38px;
  width: 48px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  cursor: pointer;
  border: 1px solid var(--border);
  background: var(--surface);
  z-index: 2; /* sicher über Titeltext */
}

.title-expand:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

/* Hilfe-Popover im Titelbereich über alle sticky/absolute Elemente legen */
:deep(.title-help .help-popover) {
  z-index: 1200 !important;
}

/* Bestehende Styles (gekürzt, unverändert wichtig) */
.input-slim {
  width: 395px;
  height: 30px;
  padding: .25rem .45rem;
  border: 1px solid var(--border);
  border-radius: .55rem;
  font-size: .9rem;
  background: var(--surface);
}

.btn-slim {
  height: 30px;
  padding: .25rem .5rem;
}

.filter-item {
  margin-left: 25px;
}

.select-slim {
  height: 30px;
  padding: .25rem .45rem;
  border: 1px solid var(--border);
  border-radius: .55rem;
  font-size: .9rem;
  background: var(--surface);
  width: 55px
}

.select-slim.select-release {
  width: 190px;
}

.inline {
  display: inline-flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.chk {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  accent-color: var(--primary);
  width: 100%;
  font-size: 14px;
}

.chk input[type="checkbox"] {
  width: 16px;
  height: 16px;
}

.muted {
  color: var(--muted);
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
  margin-bottom: -12px;
}

/* Nur den "Entfernen"-Button im Zeitdialog nach links schieben */
.btn.time-dlg__remove-btn {
  margin-right: auto;
  background: color-mix(in srgb, var(--danger) 80%, var(--surface));
  border: 1px solid var(--danger);
  color: var(--border);
}

.btn.time-dlg__remove-btn:hover {
  background: var(--danger);
}


.release-row {
  grid-column: 3 / -1;
  display: grid;
  grid-template-columns: 500px auto;
  gap: 8px;
  align-items: center;
  padding: 4px 0 2px 0;
}

.release-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.spinner {
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid var(--border);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.btn {
  padding: .35rem .6rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
}

.btn-primary {
  background: var(--primary);
  color: #fff;
  border-color: var(--primary);
}

.btn-warn {
  background: var(--warn);
  color: #fff;
  border-color: var(--warn);
}

.mt-12 {
  margin-top: 12px;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

:deep(input.flatpickr-datetime) {
  box-sizing: border-box;
  height: 28px;
  padding: 3px;
  border: 1px solid #d0d5dd;
  border-radius: .55rem;
  line-height: 1.25;
  font-size: 14px;
  background: #fff;
  width: 170px;
}

:deep(input.flatpickr-datetime::placeholder) {
  color: #d6d6d6;
}

:deep(input.flatpickr-datetime.only--time) {
  width: 120px;
}

:deep(.dialog-footer) {
  justify-content: flex-end;
  display: flex;
  border-top: 1px solid #eee;
}

.attendance-dialog.hide-footer :deep(.dialog-footer) {
  display: none !important;
}

/* Warn-Hervorhebung für Zeilen mit akzeptierter Meldung */
.att-row.row-warn .sticky-name {
  background: color-mix(in srgb, var(--warn) 12%, var(--surface));
}

.release-touch-btn {
  padding: 0 20px;
  background-color: rgba(245, 158, 11, 0.14);
  height: 32px;
  margin-right: 10px;
  text-align: center;
  border-color: rgba(245, 158, 11, 0.24)
}

.release-touch-btn:hover {
  background: rgba(245, 158, 11, 0.24)
}

.release-touch-btn:disabled {
  border: 1px solid var(--border);
  background: var(--surface);
  color: #ccc;
}


.badge-notice {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border: 1px solid var(--border);
  border-radius: 999px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--text);
}

.badge-notice i {
  color: var(--info);
}

/* Eingefärbte Spalten: Kommen/Gehen (Header und Body) */
.att-row .col-time {
  background: whitesmoke;
  padding: 3px 5px;
}

.att-header .col-head.col-time {
  background: whitesmoke;
  padding: 2px 0;
}

/* Responsive Viewports – Tablet-optimiert:
   ≥1400px: Standard (oder größer)
   1200–1399px, 1024–1199px, 980–1023px, 768–979px: abgestufte Spalten
   <768px: kein Row-Scroll; Dialog selbst erhält fixe Mindestbreite (nicht fullscreen) */
@media (min-width: 1400px) {
  .att-header,
  .att-row {
    /* Schüler | Hinweise | Kommen | Gehen | Fehlzeit */
    grid-template-columns: 2fr 280px 240px 240px 110px;
  }

  .release-row {
    grid-template-columns: 520px auto;
  }
}

@media (min-width: 1200px) and (max-width: 1399px) {
  .att-header,
  .att-row {
    /* Schüler | Hinweise | Kommen | Gehen | Fehlzeit */
    grid-template-columns: 1.8fr 250px 220px 220px 100px;
  }

  .release-row {
    grid-template-columns: 500px auto;
  }
}

@media (min-width: 1024px) and (max-width: 1199px) {
  .att-header,
  .att-row {
    /* Schüler | Hinweise | Kommen | Gehen | Fehlzeit */
    grid-template-columns: 1.6fr 220px 200px 200px 90px;
  }

  .release-row {
    grid-template-columns: 460px auto;
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .att-header,
  .att-row {
    /* Schüler | Hinweise | Kommen | Gehen | Fehlzeit */
    grid-template-columns: 1.5fr 210px 190px 190px 90px;
  }

  .release-row {
    grid-template-columns: 440px auto;
  }

  .bulk-actions-item.release-actions-items {
    margin-left: 0 !important;
  }
}

/* Zusatzregeln:
   - Fullscreen-Umschalter unter 1600px ausblenden (außer im Vollbild, damit Rückweg möglich bleibt)
   - Unter 1024px feste Dialogbreite 1050px (nicht im Vollbild) */
@media (max-width: 1599px) {
  .title-expand {
    display: none;
  }

  .as-fullscreen .title-expand {
    display: inline-flex;
  }
}

/* iPad-View: Dialog vollflächig mit 10px Außenabstand */
@media (min-width: 768px) and (max-width: 1366px) and (orientation: portrait) {
  .input-slim {
    width: auto;
  }

  .attendance-dialog :deep(.dialog) {
    margin: 10px !important;
    width: calc(100vw - 20px) !important;
    height: calc(100vh - 20px) !important;

    min-width: 0 !important;
    min-height: 0 !important;
    max-width: calc(100vw - 20px) !important;
    max-height: calc(100vh - 20px) !important;

    border-radius: 10px;

  }

  .toolbar-actions {
    position: relative;
    display: flex;
    width: 100%;
  }
}

/* Tablet-/iPad-View (Portrait + Landscape) NUR für den großen Attendance-Dialog:
   Dialog vollflächig mit 10px Außenabstand, Toolbar-Actions in den Fluss holen */
@media (min-width: 768px) and (max-width: 1366px) and (max-height: 1024px) {
  .attendance-dialog :deep(.dialog) {
    /* Außenabstand */
    margin: 10px !important;

    /* Fläche füllen */
    width: calc(100vw - 20px) !important;
    height: calc(100vh - 20px) !important;

    /* starre Min-/Max-Werte aushebeln */
    min-width: 0 !important;
    min-height: 0 !important;
    max-width: calc(100vw - 20px) !important;
    max-height: calc(100vh - 20px) !important;

    border-radius: 10px;
  }

  /* Toolbar-Actions im Tablet-Modus nicht absolut überlappen lassen */
  .attendance-dialog .toolbar-actions {
    position: static;
    display: inline-flex;
    margin-left: auto;
    margin-top: 8px;
  }
}

/* Touch-Zellen-Button */
.att-cell-btn {
  width: 100%;
  min-height: 24px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
}

.att-cell-btn.checkedTime {
  background: color-mix(in srgb, var(--primary) 20%, transparent);
  box-shadow: 3px 3px 3px -2px inset lightseagreen;
}


.att-cell-btn:disabled {
  opacity: .3;
  cursor: default;
}

.att-cell-btn:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.att-cell-btn__label {
  font-weight: 600;
  color: var(--text);
}

.att-cell-btn__time {
  font-variant-numeric: tabular-nums;
  font-size: 10px;
  text-align: left;
}

/* Zeitdialog */
.time-dlg {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.time-dlg__row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: end;
  margin-top: 6px;
}

.time-dlg__sep {
  align-self: center;
  font-weight: 600;
  color: var(--muted);
  padding-bottom: 6px;
}

.time-dlg__input {
  height: 40px;
  padding: 8px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  font-size: 16px;
  text-align: center;
}

.time-dlg__input:focus {
  box-shadow: 0 0 0 2px var(--focus);
  border-color: var(--primary);
}

/* Zeilen-Hervorhebung bei Auswahl (Bulk-Checkboxen) */
.att-row.row-selected-inout,
.att-row.row-selected-inout > .sticky-col.sticky-name {
  background: color-mix(in srgb, var(--info) 12%, var(--surface));
}

.att-row.row-selected-release,
.att-row.row-selected-release > .sticky-col.sticky-name {
  background: color-mix(in srgb, var(--warn) 14%, var(--surface));
}

/* Wenn beides selektiert ist: Entlassen-Farbe gewinnt */
.att-row.row-selected-inout.row-selected-release,
.att-row.row-selected-inout.row-selected-release > .sticky-col.sticky-name {
  background: color-mix(in srgb, var(--warn) 16%, var(--surface));
}

/* Nicht nur Farbe: dezente Kontur für bessere Erkennbarkeit */
.att-row.row-selected-inout,
.att-row.row-selected-release {
  outline: 1px solid color-mix(in srgb, var(--border-strong) 55%, transparent);
}

/* Klick auf Uhrzeit soll die Checkbox (Label) toggeln */
.chk.release-chk {
  cursor: pointer;
  user-select: none;
}

/* Falls die Uhrzeit aktuell Klicks abfängt: durchreichen an das <label> */
.chk.release-chk .release-time {
  pointer-events: none;
}

/* Optional: auch die gesamte Release-Zelle klickbar wirken lassen */
.release-cell {
  cursor: pointer;
}

.release-cell .chk {
  width: 90px;
}

.release-cell .chk .time-text {
  font-weight: 600;
  color: var(--warn);
}
</style>